import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import dataService from "../service/data/DataService";
import { onAuthStateChanged, getAuth } from "firebase/auth";
import { useAuth } from "./AuthContext";
import { useDisasterContext } from "../pages/Disasters/DisasterContext";
import partnerProfileService from "../service/partner/PartnerProfileService";
import centerService from "../service/center/CenterService";
import {
  getFirestore,
  doc,
  getDoc,
  collection,
  getDocs,
  updateDoc,
  query,
  and,
  or,
  where,
  onSnapshot,
} from "firebase/firestore";
import Popup from "./Popup";
import { encrypt, decrypt } from "../../src/service/encryption/Encryption";
import InviteFriendDialog from "./InviteDialog";
import { useNotifications } from "./Notifications/useNotifications";
import verticalDot from "./../assets/vertical-dot.svg";
import RecoveryTrackerOptions from "./RecoveryTrackerOptions";
import { db } from "../firebase";
import packageJson from "../../package.json";

const LeftNavPane = ({ className = "mt-[4px]" }) => {
  const grayMenuItems = [
    // "/communication",
    // "/citizensearch",
    // "/partnergranttracker",
    // "/reportgenerator",
  ];

  const { activeDisasterTab, setActiveDisasterTab } = useDisasterContext();
  const [userId, setUserId] = useState(null);
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  const [activeLink, setActiveLink] = useState("/home");
  const [openSubmenus, setOpenSubmenus] = useState([]);
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [partner, setPartner] = useState(null);
  const [center, setCenter] = useState(null);
  const [admin, setAdmin] = useState(null);
  const { role, logout } = useAuth();
  const [showInvite, setShowInvite] = useState(false);
  const [showBlur, setShowBlur] = useState(false);
  const selectedUserId = sessionStorage.getItem("userId");
  const { emergencyNotification } = useNotifications();
  const hasEmergencyBanner =
    emergencyNotification && emergencyNotification.length > 0;
  const [isCitizenNotActive, setIsCitizenNotActive] = useState(false);
  // const [showOptions, setShowOptions] = useState(false);
  // const [optionsPosition, setOptionsPosition] = useState({ x: 0, y: 0 });
  // const [accessOptions, setAccessOptions] = useState([]);
  // const [serverVersion, setServerVersion] = useState(null);

  // useEffect(() => {
  //   const fetchSystemsCode = async () => {
  //     try {
  //       const q = query(
  //         collection(db, "systems_code"),
  //         where("type", "==", "Version")
  //       );
  //       const querySnapshot = await getDocs(q);
  //       const newServerVersion = querySnapshot.docs[0].data().value;
  //       setServerVersion(newServerVersion);

  //       const lastRefreshedVersion = localStorage.getItem("lastVersion");

  //       // Only refresh if server version is different from both:
  //       // 1. Current package version
  //       // 2. Last refreshed version
  //       if (
  //         newServerVersion !== packageJson.version &&
  //         newServerVersion !== lastRefreshedVersion
  //       ) {
  //         //alert("Version mismatch refreshing");
  //         localStorage.setItem("lastVersion", newServerVersion);
  //         window.location.reload();
  //       }
  //     } catch (error) {
  //       console.error("Error fetching version:", error);
  //     }
  //   };

  //   fetchSystemsCode();
  //   // Set up real-time listener for version changes
  //   const unsubscribe = onSnapshot(
  //     query(collection(db, "systems_code"), where("type", "==", "Version")),
  //     (snapshot) => {
  //       if (!snapshot.empty) {
  //         const newVersion = snapshot.docs[0].data().value;
  //         setServerVersion(newVersion);
  //       }
  //     }
  //   );

  //   // Cleanup listener
  //   return () => unsubscribe();
  // }, [serverVersion]);

  useEffect(() => {
    if (userId) {
      fetchData();
    }
  }, [userId]);

  // Redirect to home if trying to access recovery plan while status not
  useEffect(() => {
    const currentPath = window.location.pathname;
    if (
      currentPath.includes("/citmyrecoveryplan") &&
      role === "citizen" &&
      data?.status !== "ACTIVE"
    ) {
      setShowBlur(true);
      setIsCitizenNotActive(true);
      navigate("/citizenprofile");
    }
  }, [window.location.pathname, role, data?.status, navigate]);

  useEffect(() => {
    setActiveLink(location.pathname);
    // Keep parent menus open when a nested route is active
    const pathParts = location.pathname.split("/");
    const openMenus = [];
    for (let i = 1; i <= pathParts.length; i++) {
      openMenus.push("/" + pathParts.slice(1, i).join("/"));
    }
    setOpenSubmenus(openMenus);

    const auth = getAuth();

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser && selectedUserId) {
        setUserId(selectedUserId);
        // const db = getFirestore();
        // const userDocRef = doc(db, "users", currentUser.uid);
        // const userDocSnap = await getDoc(userDocRef);
        // if (userDocSnap.exists()) {
        //   const userData = userDocSnap.data();
        //   if (userData.status === "registered") {
        //     alert("Please login to continue");
        //     logout();
        //     navigate("/login");
        //   }
        // }
      } else {
        setData(null);
        console.log("No user is logged in");
      }
    });

    return () => unsubscribe();
  }, [location]);

  const getPartnerByUserId = async () => {
    const p = await partnerProfileService.getPartnerByUserIdInUsers(userId);
    setPartner(p);
    sessionStorage.setItem("partner", JSON.stringify(p));
  };

  const getCenterByCenterId = async (centerId) => {
    const center = await centerService.getCenterById(centerId);
    setCenter(center);
    sessionStorage.setItem("center", JSON.stringify(center));
  };

  const getAdminByAdminId = async (adminId) => {
    const db = getFirestore();
    const adminRef = doc(db, "users", adminId);
    const adminSnap = await getDoc(adminRef);
    if (adminSnap.exists()) {
      const admin = adminSnap.data();
      setAdmin(admin);
      sessionStorage.setItem(
        "admin",
        JSON.stringify({ ...admin, id: adminId })
      );
    }
  };

  const isLinkOrSubmenuActive = (link, activeLink) => {
    return activeLink.startsWith(link.to);
  };

  const generateRandomString = (length) => {
    const characters =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";
    for (let i = 0; i < length; i++) {
      result += characters.charAt(
        Math.floor(Math.random() * characters.length)
      );
    }
    return result;
  };

  const fetchData = async () => {
    const userData = await dataService.getUserProfile("users", userId);

    if (userData) {
      setData(userData);
      if (role === "partner") {
        await getPartnerByUserId();
      } else if (role === "center") {
        await getCenterByCenterId(userData.center_id);
      } else if (role === "admin") {
        await getAdminByAdminId(userData.id);
      }
    }
    setLoading(false);
  };

  const handleLinkClick = (link, isSubmenu = false) => {
    if (link.to === "/citmyrecoveryplan") {
      // Find the "Disasters" submenu
      const disastersSubmenu = link.submenus.find(
        (sub) => sub.label === "Disasters"
      );
      if (disastersSubmenu && disastersSubmenu.submenus) {
        // Find the "Immediate Response Survey" tab
        const immediateResponseTab = disastersSubmenu.submenus.find(
          (sub) => sub.label === "Immediate Response Survey"
        );
        if (immediateResponseTab) {
          setActiveLink(immediateResponseTab.to);
          setActiveDisasterTab(immediateResponseTab.label);
          navigate(immediateResponseTab.to);
        }
      }

      // Open the "My Recovery Plan" and "Disasters" menus
      setOpenSubmenus((prev) => {
        const newOpenSubmenus = [...prev];
        if (!newOpenSubmenus.includes(link.to)) {
          newOpenSubmenus.push(link.to);
        }
        if (
          disastersSubmenu &&
          !newOpenSubmenus.includes(disastersSubmenu.to)
        ) {
          newOpenSubmenus.push(disastersSubmenu.to);
        }
        return newOpenSubmenus;
      });
    } else if (isSubmenu && link.label !== "Disasters") {
      setActiveLink(link.to);
      setActiveDisasterTab(link.label);
      navigate(link.to);
    } else if (!isSubmenu && link.label !== "Disasters") {
      // Toggle other main menus as before
      setOpenSubmenus((prev) =>
        prev.includes(link.to)
          ? prev.filter((item) => item !== link.to)
          : [...prev, link.to]
      );
      setActiveLink(link.to);
      navigate(link.to);
    }
  };

  let links = [
    {
      to: "/home",
      label: "Home",
      svg: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="19"
          height="21"
          viewBox="0 0 19 21"
          fill="none"
        >
          <path
            d="M6.63478 19.2733V16.2156C6.63478 15.4351 7.27217 14.8023 8.05844 14.8023H10.9326C11.3102 14.8023 11.6723 14.9512 11.9393 15.2163C12.2063 15.4813 12.3563 15.8408 12.3563 16.2156V19.2733C12.3539 19.5978 12.4821 19.9099 12.7124 20.1402C12.9427 20.3705 13.2561 20.5 13.5829 20.5H15.5438C16.4596 20.5023 17.3388 20.1428 17.9872 19.5008C18.6356 18.8588 19 17.987 19 17.0778V8.36686C19 7.63246 18.6721 6.93584 18.1046 6.46467L11.434 1.17587C10.2737 0.248562 8.61111 0.278502 7.48539 1.24698L0.967012 6.46467C0.372741 6.92195 0.0175523 7.62064 0 8.36686V17.0689C0 18.9639 1.54738 20.5 3.45617 20.5H5.37229C6.05123 20.5 6.603 19.9562 6.60792 19.2822L6.63478 19.2733Z"
            fill={activeLink === "/home" ? "#FFFFFF" : "#003461"}
          />
        </svg>
      ),
    },
    {
      to: "/reportgenerator",
      label: "Report Generator",
      svg: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="21"
          viewBox="0 0 18 21"
          fill={activeLink === "/reportgenerator" ? "#FFFFFF" : "#003461"}
        >
          <path
            opacity="0.4"
            d="M13.191 0.5H4.81C1.77 0.5 0 2.28 0 5.33V15.66C0 18.76 1.77 20.5 4.81 20.5H13.191C16.28 20.5 18 18.76 18 15.66V5.33C18 2.28 16.28 0.5 13.191 0.5Z"
            fill={activeLink === "/reportgenerator" ? "#FFFFFF" : "#003461"}
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M5.07996 5.15002V5.16002C4.64896 5.16002 4.29996 5.51002 4.29996 5.94002C4.29996 6.37002 4.64896 6.72002 5.07996 6.72002H8.06896C8.49996 6.72002 8.84996 6.37002 8.84996 5.92902C8.84996 5.50002 8.49996 5.15002 8.06896 5.15002H5.07996ZM12.92 11.24H5.07996C4.64896 11.24 4.29996 10.89 4.29996 10.46C4.29996 10.03 4.64896 9.67902 5.07996 9.67902H12.92C13.35 9.67902 13.7 10.03 13.7 10.46C13.7 10.89 13.35 11.24 12.92 11.24ZM12.92 15.81H5.07996C4.77996 15.85 4.48996 15.7 4.32996 15.45C4.16996 15.19 4.16996 14.86 4.32996 14.61C4.48996 14.35 4.77996 14.21 5.07996 14.24H12.92C13.319 14.28 13.62 14.62 13.62 15.03C13.62 15.429 13.319 15.77 12.92 15.81Z"
            fill={activeLink === "/reportgenerator" ? "#FFFFFF" : "#003461"}
          />
        </svg>
      ),
    },
    {
      to: "/citizenprofile",
      label: "Citizen Profile",
      svg: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"
            fill={activeLink === "/citizenprofile" ? "#FFFFFF" : "#003461"}
          />
        </svg>
      ),
    },
    {
      to: "/partnerprofile",
      label: "Partner Profile",
      svg: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"
            fill={activeLink === "/partnerprofile" ? "#FFFFFF" : "#003461"}
          />
        </svg>
      ),
    },
    {
      to: "/employees",
      label: "Employees",
      svg: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="23"
          height="17"
          viewBox="0 0 23 17"
          fill="none"
        >
          {" "}
          <path
            d="M11.4488 11.04C7.99884 11.04 5.08789 11.6038 5.08789 13.7795C5.08789 15.9562 8.01765 16.5001 11.4488 16.5001C14.8988 16.5001 17.8098 15.9364 17.8098 13.7606C17.8098 11.584 14.88 11.04 11.4488 11.04Z"
            fill={activeLink === "/employees" ? "#FFFFFF" : "#003461"}
          />{" "}
          <path
            opacity="0.4"
            d="M11.4495 8.96703C13.7856 8.96703 15.6588 7.08307 15.6588 4.73351C15.6588 2.38306 13.7856 0.5 11.4495 0.5C9.11342 0.5 7.24023 2.38306 7.24023 4.73351C7.24023 7.08307 9.11342 8.96703 11.4495 8.96703Z"
            fill={activeLink === "/employees" ? "#FFFFFF" : "#003461"}
          />{" "}
          <path
            opacity="0.4"
            d="M20.5886 5.71972C21.193 3.34225 19.4209 1.20703 17.1645 1.20703C16.9192 1.20703 16.6846 1.23405 16.4554 1.27998C16.4249 1.28718 16.3909 1.30249 16.373 1.32951C16.3524 1.36373 16.3676 1.40966 16.39 1.43937C17.0678 2.39577 17.4573 3.56018 17.4573 4.81016C17.4573 6.0079 17.1001 7.12459 16.4733 8.05126C16.4088 8.14672 16.4661 8.2755 16.5798 8.29531C16.7374 8.32323 16.8986 8.33763 17.0634 8.34214C18.7064 8.38536 20.1811 7.32181 20.5886 5.71972Z"
            fill={activeLink === "/employees" ? "#FFFFFF" : "#003461"}
          />{" "}
          <path
            d="M22.3094 11.3165C22.0086 10.6717 21.2824 10.2295 20.1783 10.0125C19.6572 9.88459 18.247 9.70447 16.9352 9.72879C16.9155 9.73149 16.9048 9.745 16.903 9.754C16.9003 9.76661 16.9057 9.78823 16.9316 9.80173C17.5378 10.1034 19.8811 11.4155 19.5865 14.1829C19.574 14.3027 19.6698 14.4063 19.7888 14.3883C20.3655 14.3054 21.8492 13.9848 22.3094 12.9861C22.5637 12.4584 22.5637 11.8451 22.3094 11.3165Z"
            fill={activeLink === "/employees" ? "#FFFFFF" : "#003461"}
          />{" "}
          <path
            opacity="0.4"
            d="M6.54508 1.27998C6.31675 1.23315 6.08126 1.20703 5.83592 1.20703C3.5795 1.20703 1.8075 3.34225 2.41279 5.71972C2.81931 7.32181 4.29403 8.38536 5.9371 8.34214C6.10185 8.33763 6.26392 8.32232 6.42061 8.29531C6.53433 8.2755 6.59164 8.14672 6.52717 8.05126C5.90039 7.12368 5.54312 6.0079 5.54312 4.81016C5.54312 3.55928 5.93352 2.39486 6.61134 1.43937C6.63283 1.40966 6.64894 1.36373 6.62745 1.32951C6.60954 1.30159 6.57642 1.28718 6.54508 1.27998Z"
            fill={activeLink === "/employees" ? "#FFFFFF" : "#003461"}
          />{" "}
          <path
            d="M2.82156 10.0127C1.71752 10.2297 0.992248 10.6719 0.691392 11.3167C0.436203 11.8453 0.436203 12.4586 0.691392 12.9872C1.15163 13.9851 2.63531 14.3066 3.21195 14.3885C3.33104 14.4065 3.42595 14.3038 3.41342 14.1832C3.11883 11.4167 5.4621 10.1046 6.06918 9.80287C6.09425 9.78846 6.09962 9.76775 6.09694 9.75424C6.09515 9.74523 6.0853 9.73173 6.0656 9.72992C4.75294 9.70471 3.34358 9.88482 2.82156 10.0127Z"
            employees
          />{" "}
        </svg>
      ),
    },
    {
      to: "/lockbox",
      label: "Lockbox",
      svg: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <rect
            x="3"
            y="11"
            width="18"
            height="11"
            rx="2"
            ry="2"
            fill={activeLink === "/lockbox" ? "#FFFFFF" : "#003461"}
            stroke={activeLink === "/lockbox" ? "#FFFFFF" : "#003461"}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M7 11V7a5 5 0 0 1 10 0v4"
            fill="none"
            stroke={activeLink === "/lockbox" ? "#FFFFFF" : "#003461"}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
    {
      to: "/partnerlockbox",
      label: "Lockbox",
      svg: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <rect
            x="3"
            y="11"
            width="18"
            height="11"
            rx="2"
            ry="2"
            fill={activeLink === "/partnerlockbox" ? "#FFFFFF" : "#003461"}
            stroke={activeLink === "/partnerlockbox" ? "#FFFFFF" : "#003461"}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M7 11V7a5 5 0 0 1 10 0v4"
            fill="none"
            stroke={activeLink === "/partnerlockbox" ? "#FFFFFF" : "#003461"}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
    {
      to: "/centerlockbox",
      label: "Lockbox",
      svg: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <rect
            x="3"
            y="11"
            width="18"
            height="11"
            rx="2"
            ry="2"
            fill={activeLink === "/centerlockbox" ? "#FFFFFF" : "#003461"}
            stroke={activeLink === "/centerlockbox" ? "#FFFFFF" : "#003461"}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M7 11V7a5 5 0 0 1 10 0v4"
            fill="none"
            stroke={activeLink === "/centerlockbox" ? "#FFFFFF" : "#003461"}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
    {
      to: "/centerprofile",
      label: "Center Profile",
      svg: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"
            fill={activeLink === "/centerprofile" ? "#FFFFFF" : "#003461"}
          />
        </svg>
      ),
    },
    {
      to: "/disasterdeclaration",
      label: "Disaster Management",
      svg: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"
            fill={
              activeLink === "/disasterdeclaration" ||
              activeLink === "/disasterdeclaration/list"
                ? "#FFFFFF"
                : "#003461"
            }
          />
        </svg>
      ),
    },
    {
      to: "/map",
      label: "Map",
      svg: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="25"
          height="25"
          viewBox="0 0 25 25"
          fill="none"
        >
          <path
            opacity="0.5"
            d="M3.5 9.20988V17.3379C3.5 18.3809 3.5 18.9029 3.783 19.2959C4.066 19.6889 4.561 19.8539 5.551 20.1839L6.716 20.5719C8.083 21.0279 8.766 21.2559 9.455 21.1629L9.5 21.1569V7.15688C9.38735 7.17366 9.27383 7.18401 9.16 7.18788C8.62 7.20688 8.086 7.02788 7.019 6.67288C5.621 6.20688 4.922 5.97388 4.39 6.21088C4.20147 6.29476 4.03228 6.41664 3.893 6.56888C3.5 6.99988 3.5 7.73588 3.5 9.20988ZM21.5 15.7899V7.66288C21.5 6.61988 21.5 6.09788 21.217 5.70488C20.934 5.31188 20.439 5.14688 19.449 4.81688L18.284 4.42888C16.917 3.97288 16.234 3.74488 15.545 3.83788L15.5 3.84288V17.8429C15.613 17.8259 15.727 17.8159 15.84 17.8119C16.38 17.7929 16.914 17.9719 17.981 18.3269C19.379 18.7929 20.078 19.0259 20.61 18.7889C20.7985 18.705 20.9677 18.5831 21.107 18.4309C21.5 17.9999 21.5 17.2639 21.5 15.7899Z"
            fill={activeLink === "/map" ? "#FFFFFF" : "#003461"}
          />
          <path
            d="M9.747 7.10977C9.66516 7.12794 9.58279 7.14361 9.5 7.15677V21.1568C10.17 21.0528 10.769 20.6538 11.942 19.8718L13.324 18.9498C14.26 18.3258 14.728 18.0138 15.254 17.8898C15.334 17.8698 15.417 17.8548 15.5 17.8428V3.84277C14.83 3.94577 14.231 4.34577 13.058 5.12677L11.676 6.04877C10.74 6.67277 10.272 6.98477 9.746 7.10877"
            fill={activeLink === "/map" ? "#FFFFFF" : "#003461"}
          />
        </svg>
      ),
    },
    {
      to: "/members",
      label: "Committee",
      svg: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="23"
          height="17"
          viewBox="0 0 23 17"
          fill="none"
        >
          {" "}
          <path
            d="M11.4488 11.04C7.99884 11.04 5.08789 11.6038 5.08789 13.7795C5.08789 15.9562 8.01765 16.5001 11.4488 16.5001C14.8988 16.5001 17.8098 15.9364 17.8098 13.7606C17.8098 11.584 14.88 11.04 11.4488 11.04Z"
            fill={
              isLinkOrSubmenuActive(
                {
                  to: "/members",
                },
                activeLink
              )
                ? "#FFFFFF"
                : "#003461"
            }
          />{" "}
          <path
            opacity="0.4"
            d="M11.4495 8.96703C13.7856 8.96703 15.6588 7.08307 15.6588 4.73351C15.6588 2.38306 13.7856 0.5 11.4495 0.5C9.11342 0.5 7.24023 2.38306 7.24023 4.73351C7.24023 7.08307 9.11342 8.96703 11.4495 8.96703Z"
            fill={
              isLinkOrSubmenuActive(
                {
                  to: "/members",
                },
                activeLink
              )
                ? "#FFFFFF"
                : "#003461"
            }
          />{" "}
          <path
            opacity="0.4"
            d="M20.5886 5.71972C21.193 3.34225 19.4209 1.20703 17.1645 1.20703C16.9192 1.20703 16.6846 1.23405 16.4554 1.27998C16.4249 1.28718 16.3909 1.30249 16.373 1.32951C16.3524 1.36373 16.3676 1.40966 16.39 1.43937C17.0678 2.39577 17.4573 3.56018 17.4573 4.81016C17.4573 6.0079 17.1001 7.12459 16.4733 8.05126C16.4088 8.14672 16.4661 8.2755 16.5798 8.29531C16.7374 8.32323 16.8986 8.33763 17.0634 8.34214C18.7064 8.38536 20.1811 7.32181 20.5886 5.71972Z"
            fill={
              isLinkOrSubmenuActive(
                {
                  to: "/members",
                },
                activeLink
              )
                ? "#FFFFFF"
                : "#003461"
            }
          />{" "}
          <path
            d="M22.3094 11.3165C22.0086 10.6717 21.2824 10.2295 20.1783 10.0125C19.6572 9.88459 18.247 9.70447 16.9352 9.72879C16.9155 9.73149 16.9048 9.745 16.903 9.754C16.9003 9.76661 16.9057 9.78823 16.9316 9.80173C17.5378 10.1034 19.8811 11.4155 19.5865 14.1829C19.574 14.3027 19.6698 14.4063 19.7888 14.3883C20.3655 14.3054 21.8492 13.9848 22.3094 12.9861C22.5637 12.4584 22.5637 11.8451 22.3094 11.3165Z"
            fill={
              isLinkOrSubmenuActive(
                {
                  to: "/members",
                },
                activeLink
              )
                ? "#FFFFFF"
                : "#003461"
            }
          />{" "}
          <path
            opacity="0.4"
            d="M6.54508 1.27998C6.31675 1.23315 6.08126 1.20703 5.83592 1.20703C3.5795 1.20703 1.8075 3.34225 2.41279 5.71972C2.81931 7.32181 4.29403 8.38536 5.9371 8.34214C6.10185 8.33763 6.26392 8.32232 6.42061 8.29531C6.53433 8.2755 6.59164 8.14672 6.52717 8.05126C5.90039 7.12368 5.54312 6.0079 5.54312 4.81016C5.54312 3.55928 5.93352 2.39486 6.61134 1.43937C6.63283 1.40966 6.64894 1.36373 6.62745 1.32951C6.60954 1.30159 6.57642 1.28718 6.54508 1.27998Z"
            fill={
              isLinkOrSubmenuActive(
                {
                  to: "/members",
                },
                activeLink
              )
                ? "#FFFFFF"
                : "#003461"
            }
          />{" "}
          <path
            d="M2.82156 10.0127C1.71752 10.2297 0.992248 10.6719 0.691392 11.3167C0.436203 11.8453 0.436203 12.4586 0.691392 12.9872C1.15163 13.9851 2.63531 14.3066 3.21195 14.3885C3.33104 14.4065 3.42595 14.3038 3.41342 14.1832C3.11883 11.4167 5.4621 10.1046 6.06918 9.80287C6.09425 9.78846 6.09962 9.76775 6.09694 9.75424C6.09515 9.74523 6.0853 9.73173 6.0656 9.72992C4.75294 9.70471 3.34358 9.88482 2.82156 10.0127Z"
            fill={
              isLinkOrSubmenuActive(
                {
                  to: "/members",
                },
                activeLink
              )
                ? "#FFFFFF"
                : "#003461"
            }
          />{" "}
        </svg>
      ),
    },
    {
      to: "/library/certificationvideos",
      label: "Library",
      svg: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="25"
          height="25"
          viewBox="0 0 25 25"
          fill="none"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M9.17233 8.04199H15.8283C19.2023 8.04199 20.8903 8.04199 21.8383 9.02899C22.7853 10.016 22.5623 11.54 22.1163 14.589L21.6943 17.481C21.3443 19.872 21.1693 21.068 20.2723 21.784C19.3753 22.5 18.0523 22.5 15.4053 22.5H9.59533C6.94933 22.5 5.62533 22.5 4.72833 21.784C3.83133 21.068 3.65633 19.872 3.30633 17.481L2.88433 14.589C2.43733 11.54 2.21433 10.016 3.16233 9.02899C4.11033 8.04199 5.79833 8.04199 9.17233 8.04199ZM8.50033 18.5C8.50033 18.086 8.87333 17.75 9.33333 17.75H15.6673C16.1273 17.75 16.5003 18.086 16.5003 18.5C16.5003 18.914 16.1273 19.25 15.6673 19.25H9.33333C8.87333 19.25 8.50033 18.914 8.50033 18.5Z"
            fill={
              isLinkOrSubmenuActive(
                {
                  to: "/library",
                  submenus: [
                    { to: "/library/certificationvideos" },
                    { to: "/library/communitycontacts" },
                    { to: "/library/localnonprofit" },
                  ],
                },
                activeLink
              )
                ? "#FFFFFF"
                : "#003461"
            }
          />
          <path
            opacity="0.4"
            d="M9.01092 2.5H15.9909C16.2239 2.5 16.4009 2.5 16.5579 2.515C17.6659 2.624 18.5719 3.29 18.9569 4.187H6.04492C6.42992 3.29 7.33692 2.624 8.44492 2.515C8.59992 2.5 8.77892 2.5 9.01092 2.5Z"
            fill={
              isLinkOrSubmenuActive(
                {
                  to: "/library",
                  submenus: [
                    { to: "/library/certificationvideos" },
                    { to: "/library/communitycontacts" },
                    { to: "/library/localnonprofit" },
                  ],
                },
                activeLink
              )
                ? "#FFFFFF"
                : "#003461"
            }
          />
          <path
            opacity="0.7"
            d="M6.81095 5.22266C5.42095 5.22266 4.28095 6.06266 3.90095 7.17566C3.89262 7.19888 3.88462 7.22221 3.87695 7.24566C4.28002 7.12783 4.69207 7.04321 5.10895 6.99266C6.18895 6.85466 7.55495 6.85466 9.14095 6.85466H16.033C17.619 6.85466 18.985 6.85466 20.065 6.99266C20.485 7.04666 20.899 7.12566 21.297 7.24566C21.2896 7.22222 21.2819 7.19889 21.274 7.17566C20.894 6.06166 19.754 5.22266 18.363 5.22266H6.81095Z"
            fill={
              isLinkOrSubmenuActive(
                {
                  to: "/library",
                  submenus: [{ to: "/library/certificationvideos" }],
                },
                activeLink
              )
                ? "#FFFFFF"
                : "#003461"
            }
          />
        </svg>
      ),
    },
    {
      to: "/database",
      label: "Database",
      svg: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="21"
          viewBox="0 0 18 21"
          fill="none"
        >
          <path
            opacity="0.4"
            d="M15.8088 7.521C15.3573 7.521 14.7592 7.511 14.0146 7.511C12.1987 7.511 10.7055 6.008 10.7055 4.175V0.959C10.7055 0.706 10.5036 0.5 10.253 0.5H4.96363C2.49517 0.5 0.5 2.526 0.5 5.009V15.784C0.5 18.389 2.59022 20.5 5.16958 20.5H13.0463C15.5058 20.5 17.5 18.487 17.5 16.002V7.971C17.5 7.717 17.299 7.512 17.0475 7.513C16.6247 7.516 16.1177 7.521 15.8088 7.521Z"
            fill={
              isLinkOrSubmenuActive(
                {
                  to: "/database",
                  submenus: [
                    { to: "/database/citizensearch" },
                    { to: "/database/contacts" },
                    { to: "/database/donorpartners" },
                    { to: "/database/localnonprofitsearch" },
                    { to: "/database/disasterrecoverysearch" },
                  ],
                },
                activeLink
              )
                ? "#FFFFFF"
                : "#003461"
            }
          />
          <path
            opacity="0.4"
            d="M13.0842 1.06725C12.7852 0.756248 12.2632 0.970248 12.2632 1.40125V4.03825C12.2632 5.14425 13.1742 6.05425 14.2802 6.05425C14.9772 6.06225 15.9452 6.06425 16.7672 6.06225C17.1882 6.06125 17.4022 5.55825 17.1102 5.25425C16.0552 4.15725 14.1662 2.19125 13.0842 1.06725Z"
            fill={
              isLinkOrSubmenuActive(
                {
                  to: "/database",
                  submenus: [
                    { to: "/database/citizensearch" },
                    { to: "/database/contacts" },
                    { to: "/database/donorpartners" },
                    { to: "/database/localnonprofitsearch" },
                    { to: "/database/disasterrecoverysearch" },
                  ],
                },
                activeLink
              )
                ? "#FFFFFF"
                : "#003461"
            }
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M5.97398 9.88756H9.35898C9.76998 9.88756 10.104 9.55456 10.104 9.14356C10.104 8.73256 9.76998 8.39856 9.35898 8.39856H5.97398C5.56298 8.39856 5.22998 8.73256 5.22998 9.14356C5.22998 9.55456 5.56298 9.88756 5.97398 9.88756ZM5.97408 14.8818H11.4181C11.8291 14.8818 12.1631 14.5488 12.1631 14.1378C12.1631 13.7268 11.8291 13.3928 11.4181 13.3928H5.97408C5.56308 13.3928 5.23008 13.7268 5.23008 14.1378C5.23008 14.5488 5.56308 14.8818 5.97408 14.8818Z"
            fill={
              isLinkOrSubmenuActive(
                {
                  to: "/opportunities",
                  submenus: [
                    { to: "/database/citizensearch" },
                    { to: "/database/contacts" },
                    { to: "/database/donorpartners" },
                    { to: "/database/localnonprofitsearch" },
                    { to: "/database/disasterrecoverysearch" },
                  ],
                },
                activeLink
              )
                ? "#FFFFFF"
                : "#003461"
            }
          />
        </svg>
      ),
      submenus: [
        {
          to: "/database/citizensearch",
          label: "Citizen Search",
        },
        {
          to: "/database/contacts",
          label: "Contacts",
        },
        {
          to: "/database/donorpartners",
          label: "Donor Partners",
        },
        {
          to: "/database/localnonprofitsearch",
          label: "Local Non-Profit Coalition Search",
        },
        // {
        //   to: "/database/disasterrecoverysearch",
        //   label: "Disaster Recovery Partners Search",
        // },
      ],
    },
    {
      to: "/center",
      label: "Center",
      svg: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="19"
          viewBox="0 0 18 19"
          fill={activeLink === "/center" ? "#FFFFFF" : "#003461"}
        >
          <path
            d="M18 13.143V14.5C18 15.314 17.619 16.01 17.09 16.557C16.567 17.099 15.857 17.541 15.058 17.891C13.456 18.591 11.314 19 9 19C6.686 19 4.544 18.592 2.942 17.891C2.143 17.541 1.433 17.099 0.91 16.557C0.425 16.057 0.0649999 15.429 0.0079999 14.701L0 14.5V13.143C0.464667 13.4083 0.962667 13.64 1.494 13.838C3.524 14.589 6.179 15.008 9 15.008C11.821 15.008 14.476 14.589 16.506 13.838C16.9047 13.69 17.2843 13.5223 17.645 13.335L18 13.143ZM0 7.643C0.464667 7.90833 0.962667 8.14 1.494 8.338C3.524 9.089 6.179 9.508 9 9.508C11.821 9.508 14.476 9.089 16.506 8.338C17.0223 8.14783 17.522 7.91537 18 7.643V10.748C17.3362 11.2609 16.5985 11.6702 15.812 11.962C14.057 12.612 11.648 13.009 9 13.009C6.353 13.009 3.944 12.612 2.188 11.962C1.40151 11.6702 0.663794 11.2609 0 10.748V7.643ZM9 0C11.314 0 13.456 0.408 15.058 1.109C15.857 1.459 16.567 1.901 17.09 2.443C17.575 2.943 17.935 3.571 17.992 4.299L18 4.5V5.248C17.3362 5.76091 16.5985 6.17023 15.812 6.462C14.057 7.112 11.648 7.509 9 7.509C6.353 7.509 3.944 7.112 2.188 6.462C1.51199 6.21085 0.8716 5.87267 0.283 5.456L0 5.248V4.5C0 3.686 0.381 2.99 0.91 2.443C1.433 1.901 2.143 1.459 2.942 1.109C4.544 0.409 6.686 0 9 0Z"
            fill={activeLink === "/center" ? "#FFFFFF" : "#003461"}
          />
        </svg>
      ),
    },
    {
      to: "/npcpartner",
      label: "NPC Partner",
      svg: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="19"
          viewBox="0 0 18 19"
          fill={activeLink === "/npcpartner" ? "#FFFFFF" : "#003461"}
        >
          <path
            d="M18 13.143V14.5C18 15.314 17.619 16.01 17.09 16.557C16.567 17.099 15.857 17.541 15.058 17.891C13.456 18.591 11.314 19 9 19C6.686 19 4.544 18.592 2.942 17.891C2.143 17.541 1.433 17.099 0.91 16.557C0.425 16.057 0.0649999 15.429 0.0079999 14.701L0 14.5V13.143C0.464667 13.4083 0.962667 13.64 1.494 13.838C3.524 14.589 6.179 15.008 9 15.008C11.821 15.008 14.476 14.589 16.506 13.838C16.9047 13.69 17.2843 13.5223 17.645 13.335L18 13.143ZM0 7.643C0.464667 7.90833 0.962667 8.14 1.494 8.338C3.524 9.089 6.179 9.508 9 9.508C11.821 9.508 14.476 9.089 16.506 8.338C17.0223 8.14783 17.522 7.91537 18 7.643V10.748C17.3362 11.2609 16.5985 11.6702 15.812 11.962C14.057 12.612 11.648 13.009 9 13.009C6.353 13.009 3.944 12.612 2.188 11.962C1.40151 11.6702 0.663794 11.2609 0 10.748V7.643ZM9 0C11.314 0 13.456 0.408 15.058 1.109C15.857 1.459 16.567 1.901 17.09 2.443C17.575 2.943 17.935 3.571 17.992 4.299L18 4.5V5.248C17.3362 5.76091 16.5985 6.17023 15.812 6.462C14.057 7.112 11.648 7.509 9 7.509C6.353 7.509 3.944 7.112 2.188 6.462C1.51199 6.21085 0.8716 5.87267 0.283 5.456L0 5.248V4.5C0 3.686 0.381 2.99 0.91 2.443C1.433 1.901 2.143 1.459 2.942 1.109C4.544 0.409 6.686 0 9 0Z"
            fill={activeLink === "/npcpartner" ? "#FFFFFF" : "#003461"}
          />
        </svg>
      ),
    },
    {
      to: "/drpartner",
      label: "Disaster Recovery Partner",
      svg: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="19"
          viewBox="0 0 18 19"
          fill={activeLink === "/drpartner" ? "#FFFFFF" : "#003461"}
        >
          <path
            d="M18 13.143V14.5C18 15.314 17.619 16.01 17.09 16.557C16.567 17.099 15.857 17.541 15.058 17.891C13.456 18.591 11.314 19 9 19C6.686 19 4.544 18.592 2.942 17.891C2.143 17.541 1.433 17.099 0.91 16.557C0.425 16.057 0.0649999 15.429 0.0079999 14.701L0 14.5V13.143C0.464667 13.4083 0.962667 13.64 1.494 13.838C3.524 14.589 6.179 15.008 9 15.008C11.821 15.008 14.476 14.589 16.506 13.838C16.9047 13.69 17.2843 13.5223 17.645 13.335L18 13.143ZM0 7.643C0.464667 7.90833 0.962667 8.14 1.494 8.338C3.524 9.089 6.179 9.508 9 9.508C11.821 9.508 14.476 9.089 16.506 8.338C17.0223 8.14783 17.522 7.91537 18 7.643V10.748C17.3362 11.2609 16.5985 11.6702 15.812 11.962C14.057 12.612 11.648 13.009 9 13.009C6.353 13.009 3.944 12.612 2.188 11.962C1.40151 11.6702 0.663794 11.2609 0 10.748V7.643ZM9 0C11.314 0 13.456 0.408 15.058 1.109C15.857 1.459 16.567 1.901 17.09 2.443C17.575 2.943 17.935 3.571 17.992 4.299L18 4.5V5.248C17.3362 5.76091 16.5985 6.17023 15.812 6.462C14.057 7.112 11.648 7.509 9 7.509C6.353 7.509 3.944 7.112 2.188 6.462C1.51199 6.21085 0.8716 5.87267 0.283 5.456L0 5.248V4.5C0 3.686 0.381 2.99 0.91 2.443C1.433 1.901 2.143 1.459 2.942 1.109C4.544 0.409 6.686 0 9 0Z"
            fill={activeLink === "/drpartner" ? "#FFFFFF" : "#003461"}
          />
        </svg>
      ),
    },
    {
      to: "/citizen",
      label: "Citizen",
      svg: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="19"
          viewBox="0 0 18 19"
          fill={activeLink === "/citizen" ? "#FFFFFF" : "#003461"}
        >
          <path
            d="M18 13.143V14.5C18 15.314 17.619 16.01 17.09 16.557C16.567 17.099 15.857 17.541 15.058 17.891C13.456 18.591 11.314 19 9 19C6.686 19 4.544 18.592 2.942 17.891C2.143 17.541 1.433 17.099 0.91 16.557C0.425 16.057 0.0649999 15.429 0.0079999 14.701L0 14.5V13.143C0.464667 13.4083 0.962667 13.64 1.494 13.838C3.524 14.589 6.179 15.008 9 15.008C11.821 15.008 14.476 14.589 16.506 13.838C16.9047 13.69 17.2843 13.5223 17.645 13.335L18 13.143ZM0 7.643C0.464667 7.90833 0.962667 8.14 1.494 8.338C3.524 9.089 6.179 9.508 9 9.508C11.821 9.508 14.476 9.089 16.506 8.338C17.0223 8.14783 17.522 7.91537 18 7.643V10.748C17.3362 11.2609 16.5985 11.6702 15.812 11.962C14.057 12.612 11.648 13.009 9 13.009C6.353 13.009 3.944 12.612 2.188 11.962C1.40151 11.6702 0.663794 11.2609 0 10.748V7.643ZM9 0C11.314 0 13.456 0.408 15.058 1.109C15.857 1.459 16.567 1.901 17.09 2.443C17.575 2.943 17.935 3.571 17.992 4.299L18 4.5V5.248C17.3362 5.76091 16.5985 6.17023 15.812 6.462C14.057 7.112 11.648 7.509 9 7.509C6.353 7.509 3.944 7.112 2.188 6.462C1.51199 6.21085 0.8716 5.87267 0.283 5.456L0 5.248V4.5C0 3.686 0.381 2.99 0.91 2.443C1.433 1.901 2.143 1.459 2.942 1.109C4.544 0.409 6.686 0 9 0Z"
            fill={activeLink === "/citizen" ? "#FFFFFF" : "#003461"}
          />
        </svg>
      ),
    },
    {
      to: "/opportunities",
      label: "Opportunities",
      svg: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="21"
          height="21"
          viewBox="0 0 21 21"
          fill="none"
        >
          <path
            opacity="0.4"
            d="M0.5 9.57812C0.55 11.9161 0.69 15.9151 0.71 16.3561C0.781 17.2991 1.142 18.2521 1.704 18.9241C2.486 19.8671 3.449 20.2881 4.792 20.2881C6.648 20.2981 8.694 20.2981 10.681 20.2981C12.676 20.2981 14.612 20.2981 16.247 20.2881C17.571 20.2881 18.564 19.8561 19.336 18.9241C19.898 18.2521 20.259 17.2891 20.31 16.3561C20.33 15.9851 20.43 11.6441 20.49 9.57812H0.5Z"
            fill={
              isLinkOrSubmenuActive(
                {
                  to: "/opportunities",
                  submenus: [
                    { to: "/opportunities/jobopportunities" },
                    { to: "/opportunities/volunteeropportunities" },
                    { to: "/opportunities/donate" },
                  ],
                },
                activeLink
              )
                ? "#FFFFFF"
                : "#003461"
            }
          />
          <path
            d="M9.74609 13.8838V15.1778C9.74609 15.5918 10.0821 15.9278 10.4961 15.9278C10.9101 15.9278 11.2461 15.5918 11.2461 15.1778V13.8838C11.2461 13.4698 10.9101 13.1338 10.4961 13.1338C10.0821 13.1338 9.74609 13.4698 9.74609 13.8838Z"
            fill={
              isLinkOrSubmenuActive(
                {
                  to: "/opportunities",
                  submenus: [
                    { to: "/opportunities/jobopportunities" },
                    { to: "/opportunities/volunteeropportunities" },
                    { to: "/opportunities/donate" },
                  ],
                },
                activeLink
              )
                ? "#FFFFFF"
                : "#003461"
            }
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M8.711 13.056C8.611 13.419 8.262 13.651 7.884 13.601C5.333 13.245 2.895 12.34 0.837 10.981C0.626 10.843 0.5 10.607 0.5 10.355V6.889C0.5 4.789 2.212 3.081 4.317 3.081H6.284C6.472 1.629 7.702 0.5 9.204 0.5H11.786C13.287 0.5 14.518 1.629 14.706 3.081H16.683C18.782 3.081 20.49 4.789 20.49 6.889V10.355C20.49 10.607 20.363 10.842 20.154 10.981C18.092 12.346 15.644 13.255 13.076 13.61C13.041 13.615 13.007 13.617 12.973 13.617C12.634 13.617 12.331 13.388 12.246 13.052C12.044 12.256 11.321 11.699 10.49 11.699C9.648 11.699 8.933 12.244 8.711 13.056ZM11.786 2H9.204C8.531 2 7.969 2.46 7.801 3.081H13.188C13.02 2.46 12.458 2 11.786 2Z"
            fill={
              isLinkOrSubmenuActive(
                {
                  to: "/opportunities",
                  submenus: [
                    { to: "/opportunities/jobopportunities" },
                    { to: "/opportunities/volunteeropportunities" },
                    { to: "/opportunities/donate" },
                  ],
                },
                activeLink
              )
                ? "#FFFFFF"
                : "#003461"
            }
          />
        </svg>
      ),
      submenus: [
        {
          to: "/opportunities/jobopportunities",
          label: "Job Opportunities",
        },
        {
          to: "/opportunities/volunteeropportunities",
          label: "Volunteer Opportunities",
        },
        {
          to: "/opportunities/donate",
          label: "Donate",
        },
      ],
    },
    {
      to: "/partneropportunities",
      label: "Opportunities",
      svg: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="21"
          height="21"
          viewBox="0 0 21 21"
          fill="none"
        >
          <path
            opacity="0.4"
            d="M0.5 9.57812C0.55 11.9161 0.69 15.9151 0.71 16.3561C0.781 17.2991 1.142 18.2521 1.704 18.9241C2.486 19.8671 3.449 20.2881 4.792 20.2881C6.648 20.2981 8.694 20.2981 10.681 20.2981C12.676 20.2981 14.612 20.2981 16.247 20.2881C17.571 20.2881 18.564 19.8561 19.336 18.9241C19.898 18.2521 20.259 17.2891 20.31 16.3561C20.33 15.9851 20.43 11.6441 20.49 9.57812H0.5Z"
            fill={
              isLinkOrSubmenuActive(
                {
                  to: "/partneropportunities",
                  submenus: [
                    { to: "/partneropportunities/partnerjobopportunities" },
                    {
                      to: "/partneropportunities/partnervolunteeropportunities",
                    },
                  ],
                },
                activeLink
              )
                ? "#FFFFFF"
                : "#003461"
            }
          />
          <path
            d="M9.74609 13.8838V15.1778C9.74609 15.5918 10.0821 15.9278 10.4961 15.9278C10.9101 15.9278 11.2461 15.5918 11.2461 15.1778V13.8838C11.2461 13.4698 10.9101 13.1338 10.4961 13.1338C10.0821 13.1338 9.74609 13.4698 9.74609 13.8838Z"
            fill={
              isLinkOrSubmenuActive(
                {
                  to: "/partneropportunities",
                  submenus: [
                    { to: "/partneropportunities/partnerjobopportunities" },
                    {
                      to: "/partneropportunities/partnervolunteeropportunities",
                    },
                  ],
                },
                activeLink
              )
                ? "#FFFFFF"
                : "#003461"
            }
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M8.711 13.056C8.611 13.419 8.262 13.651 7.884 13.601C5.333 13.245 2.895 12.34 0.837 10.981C0.626 10.843 0.5 10.607 0.5 10.355V6.889C0.5 4.789 2.212 3.081 4.317 3.081H6.284C6.472 1.629 7.702 0.5 9.204 0.5H11.786C13.287 0.5 14.518 1.629 14.706 3.081H16.683C18.782 3.081 20.49 4.789 20.49 6.889V10.355C20.49 10.607 20.363 10.842 20.154 10.981C18.092 12.346 15.644 13.255 13.076 13.61C13.041 13.615 13.007 13.617 12.973 13.617C12.634 13.617 12.331 13.388 12.246 13.052C12.044 12.256 11.321 11.699 10.49 11.699C9.648 11.699 8.933 12.244 8.711 13.056ZM11.786 2H9.204C8.531 2 7.969 2.46 7.801 3.081H13.188C13.02 2.46 12.458 2 11.786 2Z"
            fill={
              isLinkOrSubmenuActive(
                {
                  to: "/partneropportunities",
                  submenus: [
                    { to: "/partneropportunities/partnerjobopportunities" },
                    {
                      to: "/partneropportunities/partnervolunteeropportunities",
                    },
                  ],
                },
                activeLink
              )
                ? "#FFFFFF"
                : "#003461"
            }
          />
        </svg>
      ),
      submenus: [
        {
          to: "/partneropportunities/partnerjobopportunities",
          label: "Job Opportunities",
        },
        {
          to: "/partneropportunities/partnervolunteeropportunities",
          label: "Volunteer Opportunities",
        },
      ],
    },
    {
      to: "/centerpartnerships",
      label: "Center Partnerships",
      svg: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="23"
          height="17"
          viewBox="0 0 23 17"
          fill="none"
        >
          {" "}
          <path
            d="M11.4488 11.04C7.99884 11.04 5.08789 11.6038 5.08789 13.7795C5.08789 15.9562 8.01765 16.5001 11.4488 16.5001C14.8988 16.5001 17.8098 15.9364 17.8098 13.7606C17.8098 11.584 14.88 11.04 11.4488 11.04Z"
            fill={
              isLinkOrSubmenuActive(
                {
                  to: "/centerpartnerships",
                  submenus: [
                    { to: "/centerpartnerships/drccenterpartnerships" },
                    { to: "/centerpartnerships/lockbox" },
                  ],
                },
                activeLink
              )
                ? "#FFFFFF"
                : "#003461"
            }
          />{" "}
          <path
            opacity="0.4"
            d="M11.4495 8.96703C13.7856 8.96703 15.6588 7.08307 15.6588 4.73351C15.6588 2.38306 13.7856 0.5 11.4495 0.5C9.11342 0.5 7.24023 2.38306 7.24023 4.73351C7.24023 7.08307 9.11342 8.96703 11.4495 8.96703Z"
            fill={
              isLinkOrSubmenuActive(
                {
                  to: "/centerpartnerships",
                  submenus: [
                    { to: "/centerpartnerships/drccenterpartnerships" },
                    { to: "/centerpartnerships/lockbox" },
                  ],
                },
                activeLink
              )
                ? "#FFFFFF"
                : "#003461"
            }
          />{" "}
          <path
            opacity="0.4"
            d="M20.5886 5.71972C21.193 3.34225 19.4209 1.20703 17.1645 1.20703C16.9192 1.20703 16.6846 1.23405 16.4554 1.27998C16.4249 1.28718 16.3909 1.30249 16.373 1.32951C16.3524 1.36373 16.3676 1.40966 16.39 1.43937C17.0678 2.39577 17.4573 3.56018 17.4573 4.81016C17.4573 6.0079 17.1001 7.12459 16.4733 8.05126C16.4088 8.14672 16.4661 8.2755 16.5798 8.29531C16.7374 8.32323 16.8986 8.33763 17.0634 8.34214C18.7064 8.38536 20.1811 7.32181 20.5886 5.71972Z"
            fill={
              isLinkOrSubmenuActive(
                {
                  to: "/centerpartnerhips",
                  submenus: [
                    { to: "/centerpartnerships/drccenterpartnerships" },
                    { to: "/centerpartnerships/lockbox" },
                  ],
                },
                activeLink
              )
                ? "#FFFFFF"
                : "#003461"
            }
          />{" "}
          <path
            d="M22.3094 11.3165C22.0086 10.6717 21.2824 10.2295 20.1783 10.0125C19.6572 9.88459 18.247 9.70447 16.9352 9.72879C16.9155 9.73149 16.9048 9.745 16.903 9.754C16.9003 9.76661 16.9057 9.78823 16.9316 9.80173C17.5378 10.1034 19.8811 11.4155 19.5865 14.1829C19.574 14.3027 19.6698 14.4063 19.7888 14.3883C20.3655 14.3054 21.8492 13.9848 22.3094 12.9861C22.5637 12.4584 22.5637 11.8451 22.3094 11.3165Z"
            fill={
              isLinkOrSubmenuActive(
                {
                  to: "/centerpartnerships",
                  submenus: [
                    { to: "/centerpartnerships/drccenterpartnerships" },
                    { to: "/centerpartnerships/lockbox" },
                  ],
                },
                activeLink
              )
                ? "#FFFFFF"
                : "#003461"
            }
          />{" "}
          <path
            opacity="0.4"
            d="M6.54508 1.27998C6.31675 1.23315 6.08126 1.20703 5.83592 1.20703C3.5795 1.20703 1.8075 3.34225 2.41279 5.71972C2.81931 7.32181 4.29403 8.38536 5.9371 8.34214C6.10185 8.33763 6.26392 8.32232 6.42061 8.29531C6.53433 8.2755 6.59164 8.14672 6.52717 8.05126C5.90039 7.12368 5.54312 6.0079 5.54312 4.81016C5.54312 3.55928 5.93352 2.39486 6.61134 1.43937C6.63283 1.40966 6.64894 1.36373 6.62745 1.32951C6.60954 1.30159 6.57642 1.28718 6.54508 1.27998Z"
            fill={
              isLinkOrSubmenuActive(
                {
                  to: "/centepartnerships",
                  submenus: [
                    { to: "/centerpartnerships/drccenterpartnerships" },
                    { to: "/centerpartnerships/lockbox" },
                  ],
                },
                activeLink
              )
                ? "#FFFFFF"
                : "#003461"
            }
          />{" "}
          <path
            d="M2.82156 10.0127C1.71752 10.2297 0.992248 10.6719 0.691392 11.3167C0.436203 11.8453 0.436203 12.4586 0.691392 12.9872C1.15163 13.9851 2.63531 14.3066 3.21195 14.3885C3.33104 14.4065 3.42595 14.3038 3.41342 14.1832C3.11883 11.4167 5.4621 10.1046 6.06918 9.80287C6.09425 9.78846 6.09962 9.76775 6.09694 9.75424C6.09515 9.74523 6.0853 9.73173 6.0656 9.72992C4.75294 9.70471 3.34358 9.88482 2.82156 10.0127Z"
            fill={
              isLinkOrSubmenuActive(
                {
                  to: "/centerpartnerships",
                  submenus: [
                    { to: "/centerpartnerships/drccenterpartnerships" },
                    { to: "/centerpartnerships/lockbox" },
                  ],
                },
                activeLink
              )
                ? "#FFFFFF"
                : "#003461"
            }
          />{" "}
        </svg>
      ),
      submenus: [
        {
          to: "/centerpartnerships/drccenterpartnerships",
          label: "Center Partnerships",
        },
      ],
    },
    {
      to: "/demographics",
      label: "Demographics",
      svg: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="25"
          height="25"
          viewBox="0 0 25 25"
          fill="none"
        >
          <path
            d="M12.5 2.5C6.977 2.5 2.5 6.977 2.5 12.5C2.5 18.023 6.977 22.5 12.5 22.5C18.023 22.5 22.5 18.023 22.5 12.5C22.5 6.977 18.023 2.5 12.5 2.5Z"
            fill={activeLink === "/demographics" ? "#FFFFFF" : "#003461"}
            opacity="0.4"
          />
          <path
            d="M12.5 6.5C13.8807 6.5 15 7.61929 15 9C15 10.3807 13.8807 11.5 12.5 11.5C11.1193 11.5 10 10.3807 10 9C10 7.61929 11.1193 6.5 12.5 6.5Z"
            fill={activeLink === "/demographics" ? "#FFFFFF" : "#003461"}
          />
          <path
            d="M17.5 16.5C17.5 18.433 15.261 20 12.5 20C9.739 20 7.5 18.433 7.5 16.5C7.5 14.567 9.739 13 12.5 13C15.261 13 17.5 14.567 17.5 16.5Z"
            fill={activeLink === "/demographics" ? "#FFFFFF" : "#003461"}
          />
        </svg>
      ),
    },
    {
      to: "/partnerships",
      label: "Partnerships",
      svg: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="23"
          height="17"
          viewBox="0 0 23 17"
          fill="none"
        >
          {" "}
          <path
            d="M11.4488 11.04C7.99884 11.04 5.08789 11.6038 5.08789 13.7795C5.08789 15.9562 8.01765 16.5001 11.4488 16.5001C14.8988 16.5001 17.8098 15.9364 17.8098 13.7606C17.8098 11.584 14.88 11.04 11.4488 11.04Z"
            fill={
              isLinkOrSubmenuActive(
                {
                  to: "/partnerships",
                },
                activeLink
              )
                ? "#FFFFFF"
                : "#003461"
            }
          />{" "}
          <path
            opacity="0.4"
            d="M11.4495 8.96703C13.7856 8.96703 15.6588 7.08307 15.6588 4.73351C15.6588 2.38306 13.7856 0.5 11.4495 0.5C9.11342 0.5 7.24023 2.38306 7.24023 4.73351C7.24023 7.08307 9.11342 8.96703 11.4495 8.96703Z"
            fill={
              isLinkOrSubmenuActive(
                {
                  to: "/partnerships",
                },
                activeLink
              )
                ? "#FFFFFF"
                : "#003461"
            }
          />{" "}
          <path
            opacity="0.4"
            d="M20.5886 5.71972C21.193 3.34225 19.4209 1.20703 17.1645 1.20703C16.9192 1.20703 16.6846 1.23405 16.4554 1.27998C16.4249 1.28718 16.3909 1.30249 16.373 1.32951C16.3524 1.36373 16.3676 1.40966 16.39 1.43937C17.0678 2.39577 17.4573 3.56018 17.4573 4.81016C17.4573 6.0079 17.1001 7.12459 16.4733 8.05126C16.4088 8.14672 16.4661 8.2755 16.5798 8.29531C16.7374 8.32323 16.8986 8.33763 17.0634 8.34214C18.7064 8.38536 20.1811 7.32181 20.5886 5.71972Z"
            fill={
              isLinkOrSubmenuActive(
                {
                  to: "/partnerships",
                },
                activeLink
              )
                ? "#FFFFFF"
                : "#003461"
            }
          />{" "}
          <path
            d="M22.3094 11.3165C22.0086 10.6717 21.2824 10.2295 20.1783 10.0125C19.6572 9.88459 18.247 9.70447 16.9352 9.72879C16.9155 9.73149 16.9048 9.745 16.903 9.754C16.9003 9.76661 16.9057 9.78823 16.9316 9.80173C17.5378 10.1034 19.8811 11.4155 19.5865 14.1829C19.574 14.3027 19.6698 14.4063 19.7888 14.3883C20.3655 14.3054 21.8492 13.9848 22.3094 12.9861C22.5637 12.4584 22.5637 11.8451 22.3094 11.3165Z"
            fill={
              isLinkOrSubmenuActive(
                {
                  to: "/partnerships",
                },
                activeLink
              )
                ? "#FFFFFF"
                : "#003461"
            }
          />{" "}
          <path
            opacity="0.4"
            d="M6.54508 1.27998C6.31675 1.23315 6.08126 1.20703 5.83592 1.20703C3.5795 1.20703 1.8075 3.34225 2.41279 5.71972C2.81931 7.32181 4.29403 8.38536 5.9371 8.34214C6.10185 8.33763 6.26392 8.32232 6.42061 8.29531C6.53433 8.2755 6.59164 8.14672 6.52717 8.05126C5.90039 7.12368 5.54312 6.0079 5.54312 4.81016C5.54312 3.55928 5.93352 2.39486 6.61134 1.43937C6.63283 1.40966 6.64894 1.36373 6.62745 1.32951C6.60954 1.30159 6.57642 1.28718 6.54508 1.27998Z"
            fill={
              isLinkOrSubmenuActive(
                {
                  to: "/partnerships",
                },
                activeLink
              )
                ? "#FFFFFF"
                : "#003461"
            }
          />{" "}
          <path
            d="M2.82156 10.0127C1.71752 10.2297 0.992248 10.6719 0.691392 11.3167C0.436203 11.8453 0.436203 12.4586 0.691392 12.9872C1.15163 13.9851 2.63531 14.3066 3.21195 14.3885C3.33104 14.4065 3.42595 14.3038 3.41342 14.1832C3.11883 11.4167 5.4621 10.1046 6.06918 9.80287C6.09425 9.78846 6.09962 9.76775 6.09694 9.75424C6.09515 9.74523 6.0853 9.73173 6.0656 9.72992C4.75294 9.70471 3.34358 9.88482 2.82156 10.0127Z"
            fill={
              isLinkOrSubmenuActive(
                {
                  to: "/partnerships",
                },
                activeLink
              )
                ? "#FFFFFF"
                : "#003461"
            }
          />{" "}
        </svg>
      ),
    },

    {
      to: "/mylocalcommunity",
      label: "Local Community",
      svg: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="23"
          height="17"
          viewBox="0 0 23 17"
          fill="none"
        >
          <path
            d="M11.4488 11.04C7.99884 11.04 5.08789 11.6038 5.08789 13.7795C5.08789 15.9562 8.01765 16.5001 11.4488 16.5001C14.8988 16.5001 17.8098 15.9364 17.8098 13.7606C17.8098 11.584 14.88 11.04 11.4488 11.04Z"
            fill={
              isLinkOrSubmenuActive(
                {
                  to: "/mylocalcommunity",
                  submenus: [
                    { to: "/mylocalcommunity/drccenter" },
                    { to: "/mylocalcommunity/comminitycontacts" },
                    { to: "/mylocalcommunity/localnonprofit" },
                  ],
                },
                activeLink
              )
                ? "#FFFFFF"
                : "#003461"
            }
          />
          <path
            opacity="0.4"
            d="M11.4495 8.96703C13.7856 8.96703 15.6588 7.08307 15.6588 4.73351C15.6588 2.38306 13.7856 0.5 11.4495 0.5C9.11342 0.5 7.24023 2.38306 7.24023 4.73351C7.24023 7.08307 9.11342 8.96703 11.4495 8.96703Z"
            fill={
              isLinkOrSubmenuActive(
                {
                  to: "/mylocalcommunity",
                  submenus: [
                    { to: "/mylocalcommunity/drccenter" },
                    { to: "/mylocalcommunity/comminitycontacts" },
                    { to: "/mylocalcommunity/localnonprofit" },
                  ],
                },
                activeLink
              )
                ? "#FFFFFF"
                : "#003461"
            }
          />
          <path
            opacity="0.4"
            d="M20.5886 5.71972C21.193 3.34225 19.4209 1.20703 17.1645 1.20703C16.9192 1.20703 16.6846 1.23405 16.4554 1.27998C16.4249 1.28718 16.3909 1.30249 16.373 1.32951C16.3524 1.36373 16.3676 1.40966 16.39 1.43937C17.0678 2.39577 17.4573 3.56018 17.4573 4.81016C17.4573 6.0079 17.1001 7.12459 16.4733 8.05126C16.4088 8.14672 16.4661 8.2755 16.5798 8.29531C16.7374 8.32323 16.8986 8.33763 17.0634 8.34214C18.7064 8.38536 20.1811 7.32181 20.5886 5.71972Z"
            fill={
              isLinkOrSubmenuActive(
                {
                  to: "/mylocalcommunity",
                  submenus: [
                    { to: "/mylocalcommunity/drccenter" },
                    { to: "/mylocalcommunity/comminitycontacts" },
                    { to: "/mylocalcommunity/localnonprofit" },
                  ],
                },
                activeLink
              )
                ? "#FFFFFF"
                : "#003461"
            }
          />
          <path
            d="M22.3094 11.3165C22.0086 10.6717 21.2824 10.2295 20.1783 10.0125C19.6572 9.88459 18.247 9.70447 16.9352 9.72879C16.9155 9.73149 16.9048 9.745 16.903 9.754C16.9003 9.76661 16.9057 9.78823 16.9316 9.80173C17.5378 10.1034 19.8811 11.4155 19.5865 14.1829C19.574 14.3027 19.6698 14.4063 19.7888 14.3883C20.3655 14.3054 21.8492 13.9848 22.3094 12.9861C22.5637 12.4584 22.5637 11.8451 22.3094 11.3165Z"
            fill={
              isLinkOrSubmenuActive(
                {
                  to: "/mylocalcommunity",
                  submenus: [
                    { to: "/mylocalcommunity/drccenter" },
                    { to: "/mylocalcommunity/comminitycontacts" },
                    { to: "/mylocalcommunity/localnonprofit" },
                  ],
                },
                activeLink
              )
                ? "#FFFFFF"
                : "#003461"
            }
          />
          <path
            opacity="0.4"
            d="M6.54508 1.27998C6.31675 1.23315 6.08126 1.20703 5.83592 1.20703C3.5795 1.20703 1.8075 3.34225 2.41279 5.71972C2.81931 7.32181 4.29403 8.38536 5.9371 8.34214C6.10185 8.33763 6.26392 8.32232 6.42061 8.29531C6.53433 8.2755 6.59164 8.14672 6.52717 8.05126C5.90039 7.12368 5.54312 6.0079 5.54312 4.81016C5.54312 3.55928 5.93352 2.39486 6.61134 1.43937C6.63283 1.40966 6.64894 1.36373 6.62745 1.32951C6.60954 1.30159 6.57642 1.28718 6.54508 1.27998Z"
            fill={
              isLinkOrSubmenuActive(
                {
                  to: "/mylocalcommunity",
                  submenus: [
                    { to: "/mylocalcommunity/drccenter" },
                    { to: "/mylocalcommunity/comminitycontacts" },
                    { to: "/mylocalcommunity/localnonprofit" },
                  ],
                },
                activeLink
              )
                ? "#FFFFFF"
                : "#003461"
            }
          />
          <path
            d="M2.82156 10.0127C1.71752 10.2297 0.992248 10.6719 0.691392 11.3167C0.436203 11.8453 0.436203 12.4586 0.691392 12.9872C1.15163 13.9851 2.63531 14.3066 3.21195 14.3885C3.33104 14.4065 3.42595 14.3038 3.41342 14.1832C3.11883 11.4167 5.4621 10.1046 6.06918 9.80287C6.09425 9.78846 6.09962 9.76775 6.09694 9.75424C6.09515 9.74523 6.0853 9.73173 6.0656 9.72992C4.75294 9.70471 3.34358 9.88482 2.82156 10.0127Z"
            fill={
              isLinkOrSubmenuActive(
                {
                  to: "/mylocalcommunity",
                  submenus: [
                    { to: "/mylocalcommunity/drccenter" },
                    { to: "/mylocalcommunity/comminitycontacts" },
                    { to: "/mylocalcommunity/localnonprofit" },
                  ],
                },
                activeLink
              )
                ? "#FFFFFF"
                : "#003461"
            }
          />
        </svg>
      ),
      submenus: [
        {
          to: "/mylocalcommunity/drccenter",
          label: "Center Information",
        },
        {
          to: "/mylocalcommunity/communitycontacts",
          label: "Community Contacts",
        },
        {
          to: "/mylocalcommunity/localnonprofit",
          label: "Local Non Profit Coalition",
        },
      ],
    },
    {
      to: "/citmyrecoveryplan",
      label: "Recovery Tracker",
      svg: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="25"
          height="25"
          viewBox="0 0 25 25"
          fill="none"
        >
          <path
            opacity="0.5"
            d="M21.5 16.498V10.498C21.5 7.67 21.5 6.256 20.621 5.377C19.853 4.609 18.675 4.512 16.5 4.5H8.5C6.325 4.512 5.147 4.609 4.379 5.377C3.5 6.256 3.5 7.67 3.5 10.498V16.498C3.5 19.327 3.5 20.741 4.379 21.62C5.257 22.498 6.672 22.498 9.5 22.498H15.5C18.328 22.498 19.743 22.498 20.621 21.62C21.5 20.74 21.5 19.327 21.5 16.498Z"
            fill={
              isLinkOrSubmenuActive(
                {
                  to: "/citmyrecoveryplan",
                  submenus: [{ to: "/citmyrecoveryplan/disasters" }],
                },
                activeLink
              )
                ? "#FFFFFF"
                : "#003461"
            }
          />
          <path
            d="M8.5 4C8.5 3.60218 8.65804 3.22064 8.93934 2.93934C9.22064 2.65804 9.60218 2.5 10 2.5H15C15.3978 2.5 15.7794 2.65804 16.0607 2.93934C16.342 3.22064 16.5 3.60218 16.5 4V5C16.5 5.39782 16.342 5.77936 16.0607 6.06066C15.7794 6.34196 15.3978 6.5 15 6.5H10C9.60218 6.5 9.22064 6.34196 8.93934 6.06066C8.65804 5.77936 8.5 5.39782 8.5 5V4Z"
            fill={
              isLinkOrSubmenuActive(
                {
                  to: "/citmyrecoveryplan",
                  submenus: [{ to: "/citmyrecoveryplan/disasters" }],
                },
                activeLink
              )
                ? "#FFFFFF"
                : "#003461"
            }
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M16.0474 10.988C16.1832 11.1333 16.2557 11.3266 16.2489 11.5254C16.2422 11.7242 16.1567 11.9122 16.0114 12.048L11.7254 16.048C11.5865 16.1777 11.4035 16.2499 11.2134 16.2499C11.0233 16.2499 10.8403 16.1777 10.7014 16.048L8.98741 14.448C8.91435 14.381 8.85529 14.3002 8.81363 14.2103C8.77197 14.1204 8.74854 14.0231 8.7447 13.9241C8.74086 13.8251 8.75669 13.7263 8.79126 13.6334C8.82583 13.5405 8.87846 13.4554 8.94612 13.383C9.01377 13.3106 9.0951 13.2523 9.18542 13.2115C9.27573 13.1707 9.37323 13.1482 9.47229 13.1453C9.57134 13.1425 9.66999 13.1592 9.76253 13.1947C9.85506 13.2302 9.93965 13.2836 10.0114 13.352L11.2134 14.474L14.9874 10.952C15.1328 10.8162 15.3261 10.7437 15.5249 10.7504C15.7237 10.7572 15.9116 10.8426 16.0474 10.988Z"
            fill={
              isLinkOrSubmenuActive(
                {
                  to: "/citmyrecoveryplan",
                  submenus: [{ to: "/citmyrecoveryplan/disasters" }],
                },
                activeLink
              )
                ? "#FFFFFF"
                : "#003461"
            }
          />
        </svg>
      ),
      additionalIcon: (
        <svg
          width="24"
          height="25"
          viewBox="0 0 24 25"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g mask="url(#mask0_9869_751)">
            <path
              d="M12 20.5C11.45 20.5 10.9792 20.3042 10.5875 19.9125C10.1958 19.5208 10 19.05 10 18.5C10 17.95 10.1958 17.4792 10.5875 17.0875C10.9792 16.6958 11.45 16.5 12 16.5C12.55 16.5 13.0208 16.6958 13.4125 17.0875C13.8042 17.4792 14 17.95 14 18.5C14 19.05 13.8042 19.5208 13.4125 19.9125C13.0208 20.3042 12.55 20.5 12 20.5ZM12 14.5C11.45 14.5 10.9792 14.3042 10.5875 13.9125C10.1958 13.5208 10 13.05 10 12.5C10 11.95 10.1958 11.4792 10.5875 11.0875C10.9792 10.6958 11.45 10.5 12 10.5C12.55 10.5 13.0208 10.6958 13.4125 11.0875C13.8042 11.4792 14 11.95 14 12.5C14 13.05 13.8042 13.5208 13.4125 13.9125C13.0208 14.3042 12.55 14.5 12 14.5ZM12 8.5C11.45 8.5 10.9792 8.30417 10.5875 7.9125C10.1958 7.52083 10 7.05 10 6.5C10 5.95 10.1958 5.47917 10.5875 5.0875C10.9792 4.69583 11.45 4.5 12 4.5C12.55 4.5 13.0208 4.69583 13.4125 5.0875C13.8042 5.47917 14 5.95 14 6.5C14 7.05 13.8042 7.52083 13.4125 7.9125C13.0208 8.30417 12.55 8.5 12 8.5Z"
              fill="#242424"
            />
          </g>
        </svg>
      ),
      submenus: [
        {
          to: "/citmyrecoveryplan/disasters",
          label: "Disasters",
          submenus: [
            {
              to: "/citmyrecoveryplan/disasters/",
              label: "Immediate Response Survey",
              readOnly: true,
            },
            {
              to: "/citmyrecoveryplan/disasters/",
              label: "Damage Assessment",
              readOnly: true,
            },
            {
              to: "/citmyrecoveryplan/disasters/",
              label: "Assistance Log",
              readOnly: true,
            },
            {
              to: "/citmyrecoveryplan/disasters/",
              label: "Unmet Needs",
              readOnly: true,
            },
            {
              to: "/citmyrecoveryplan/disasters/",
              label: "Contacts",
              readOnly: true,
            },
            {
              to: "/citmyrecoveryplan/disasters/",
              label: "Journals",
              readOnly: true,
            },
            {
              to: "/citmyrecoveryplan/disasters/",
              label: "Lockbox",
              readOnly: true,
            },
          ],
        },
      ],
    },
    {
      to: "/cenmyrecoveryplan",
      label: "Recovery Tracker",
      svg: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="25"
          height="25"
          viewBox="0 0 25 25"
          fill="none"
        >
          <path
            opacity="0.5"
            d="M21.5 16.498V10.498C21.5 7.67 21.5 6.256 20.621 5.377C19.853 4.609 18.675 4.512 16.5 4.5H8.5C6.325 4.512 5.147 4.609 4.379 5.377C3.5 6.256 3.5 7.67 3.5 10.498V16.498C3.5 19.327 3.5 20.741 4.379 21.62C5.257 22.498 6.672 22.498 9.5 22.498H15.5C18.328 22.498 19.743 22.498 20.621 21.62C21.5 20.74 21.5 19.327 21.5 16.498Z"
            fill={
              isLinkOrSubmenuActive(
                {
                  to: "/cenmyrecoveryplan",
                  submenus: [
                    // { to: "/cenmyrecoveryplan/contacts" },
                    { to: "/cenmyrecoveryplan/demographics" },
                    { to: "/cenmyrecoveryplan/emergencydocuments" },
                    // { to: "/cenmyrecoveryplan/localnonprofit" },
                    // { to: "/cenmyrecoveryplan/disasterrecovery" },
                  ],
                },
                activeLink
              )
                ? "#FFFFFF"
                : "#003461"
            }
          />
          <path
            d="M8.5 4C8.5 3.60218 8.65804 3.22064 8.93934 2.93934C9.22064 2.65804 9.60218 2.5 10 2.5H15C15.3978 2.5 15.7794 2.65804 16.0607 2.93934C16.342 3.22064 16.5 3.60218 16.5 4V5C16.5 5.39782 16.342 5.77936 16.0607 6.06066C15.7794 6.34196 15.3978 6.5 15 6.5H10C9.60218 6.5 9.22064 6.34196 8.93934 6.06066C8.65804 5.77936 8.5 5.39782 8.5 5V4Z"
            fill={
              isLinkOrSubmenuActive(
                {
                  to: "/cenmyrecoveryplan",
                  submenus: [
                    // { to: "/cenmyrecoveryplan/contacts" },
                    { to: "/cenmyrecoveryplan/demographics" },
                    { to: "/cenmyrecoveryplan/emergencydocuments" },
                    // { to: "/cenmyrecoveryplan/localnonprofit" },
                    // { to: "/cenmyrecoveryplan/disasterrecovery" },
                  ],
                },
                activeLink
              )
                ? "#FFFFFF"
                : "#003461"
            }
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M16.0474 10.988C16.1832 11.1333 16.2557 11.3266 16.2489 11.5254C16.2422 11.7242 16.1567 11.9122 16.0114 12.048L11.7254 16.048C11.5865 16.1777 11.4035 16.2499 11.2134 16.2499C11.0233 16.2499 10.8403 16.1777 10.7014 16.048L8.98741 14.448C8.91435 14.381 8.85529 14.3002 8.81363 14.2103C8.77197 14.1204 8.74854 14.0231 8.7447 13.9241C8.74086 13.8251 8.75669 13.7263 8.79126 13.6334C8.82583 13.5405 8.87846 13.4554 8.94612 13.383C9.01377 13.3106 9.0951 13.2523 9.18542 13.2115C9.27573 13.1707 9.37323 13.1482 9.47229 13.1453C9.57134 13.1425 9.66999 13.1592 9.76253 13.1947C9.85506 13.2302 9.93965 13.2836 10.0114 13.352L11.2134 14.474L14.9874 10.952C15.1328 10.8162 15.3261 10.7437 15.5249 10.7504C15.7237 10.7572 15.9116 10.8426 16.0474 10.988Z"
            fill={
              isLinkOrSubmenuActive(
                {
                  to: "/cenmyrecoveryplan",
                  submenus: [
                    // { to: "/cenmyrecoveryplan/contacts" },
                    { to: "/cenmyrecoveryplan/demographics" },
                    { to: "/cenmyrecoveryplan/emergencydocuments" },
                    // { to: "/cenmyrecoveryplan/localnonprofit" },
                    // { to: "/cenmyrecoveryplan/disasterrecovery" },
                  ],
                },
                activeLink
              )
                ? "#FFFFFF"
                : "#003461"
            }
          />
        </svg>
      ),
      submenus: [
        // {
        //   to: "/cenmyrecoveryplan/contacts",
        //   label: "Contacts",
        // },
        {
          to: "/cenmyrecoveryplan/demographics",
          label: "Demographics",
        },
        {
          to: "/cenmyrecoveryplan/emergencydocuments",
          label: "Emergency Management Documents",
        },
        // {
        //   to: "/cenmyrecoveryplan/localnonprofit",
        //   label: "Local Non-Profit Coalition",
        // },
        // {
        //   to: "/cenmyrecoveryplan/disasterrecovery",
        //   label: "Disaster Recovery Partners",
        // },
      ],
    },

    // {
    //   to: "/granttracker",
    //   label: "Grant Tracker",
    //   svg: (
    //     <svg
    //       xmlns="http://www.w3.org/2000/svg"
    //       width="20"
    //       height="21"
    //       viewBox="0 0 20 21"
    //       fill="none"
    //     >
    //       <path
    //         fill-rule="evenodd"
    //         clip-rule="evenodd"
    //         d="M13.2428 3.23756C13.2428 5.45855 15.0459 7.25902 17.2702 7.25902C17.5151 7.25782 17.7594 7.23431 18 7.18878V15.1615C18 18.5156 16.0215 20.5 12.6624 20.5H5.34636C1.97851 20.5 0 18.5156 0 15.1615V7.8561C0 4.50195 1.97851 2.5 5.34636 2.5H13.3131C13.2659 2.743 13.2423 2.99001 13.2428 3.23756ZM11.15 13.3966L14.0078 9.70878V9.69122C14.2525 9.36247 14.1901 8.89889 13.8671 8.64634C13.7108 8.52568 13.5122 8.47345 13.3167 8.50159C13.1211 8.52972 12.9453 8.6358 12.8295 8.79561L10.4201 11.8951L7.6766 9.73512C7.51997 9.61309 7.32071 9.55922 7.12381 9.58565C6.92691 9.61208 6.74898 9.7166 6.63019 9.87561L3.67562 13.6863C3.57177 13.8158 3.51586 13.9771 3.51734 14.1429C3.5002 14.4781 3.71187 14.7826 4.03238 14.8838C4.35288 14.985 4.70138 14.8573 4.88031 14.5732L7.35125 11.3771L10.0948 13.5283C10.2508 13.6541 10.4514 13.7111 10.6504 13.6863C10.8494 13.6615 11.0297 13.5569 11.15 13.3966Z"
    //         fill={activeLink === "/granttracker" ? "#FFFFFF" : "#003461"}
    //       />

    //       <circle
    //         opacity="0.4"
    //         cx="17.5"
    //         cy="3"
    //         r="2.5"
    //         fill={activeLink === "/granttracker" ? "#FFFFFF" : "#003461"}
    //       />
    //     </svg>
    //   ),
    // },
    {
      to: "/communication",
      label: "Communication",
      svg: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="25"
          height="25"
          viewBox="0 0 25 25"
          fill="none"
        >
          <path
            opacity="0.5"
            d="M12.5 22.5C18.023 22.5 22.5 18.023 22.5 12.5C22.5 6.977 18.023 2.5 12.5 2.5C6.977 2.5 2.5 6.977 2.5 12.5C2.5 14.1 2.876 15.612 3.543 16.953C3.721 17.309 3.78 17.716 3.677 18.101L3.082 20.327C3.02307 20.5473 3.02312 20.7792 3.08216 20.9995C3.14119 21.2197 3.25712 21.4206 3.41831 21.5819C3.57951 21.7432 3.7803 21.8593 4.00053 21.9184C4.22075 21.9776 4.45267 21.9778 4.673 21.919L6.899 21.323C7.28541 21.2258 7.69403 21.2731 8.048 21.456C9.43092 22.1446 10.9551 22.5021 12.5 22.5Z"
            fill={activeLink === "/communication" ? "#FFFFFF" : "#003461"}
          />
          <path
            d="M16.0304 9.97032C16.1708 10.1109 16.2497 10.3016 16.2497 10.5003C16.2497 10.6991 16.1708 10.8897 16.0304 11.0303L12.0304 15.0303C11.8916 15.1692 11.704 15.2482 11.5076 15.2502C11.3113 15.2523 11.122 15.1773 10.9804 15.0413L8.98039 13.1213C8.8369 12.9834 8.75409 12.7941 8.75015 12.5952C8.74621 12.3962 8.82147 12.2038 8.95939 12.0603C9.0973 11.9168 9.28656 11.834 9.48554 11.8301C9.68451 11.8261 9.87691 11.9014 10.0204 12.0393L11.4904 13.4503L14.9704 9.97032C15.111 9.82987 15.3016 9.75098 15.5004 9.75098C15.6991 9.75098 15.8898 9.82987 16.0304 9.97032Z"
            fill={activeLink === "/communication" ? "#FFFFFF" : "#003461"}
          />
        </svg>
      ),
      disabled: false, //change this depending
      labelStyle: { color: "#003461" }, // Add this line to grey out the label
    },
    // ... existing code ...

    {
      to: "/citizensearch",
      label: "Citizen Search",
      svg: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="21"
          viewBox="0 0 20 21"
          fill="none"
        >
          <ellipse
            cx="8.59922"
            cy="9.15324"
            rx="8.59922"
            ry="8.65324"
            fill={activeLink === "/citizensearch" ? "#FFFFFF" : "#003461"}
          />
          <path
            opacity="0.4"
            d="M18.6745 20.4553C18.3405 20.4445 18.0228 20.307 17.7853 20.0705L15.7488 17.6902C15.3122 17.2909 15.2765 16.6123 15.6688 16.1689C15.8524 15.9831 16.102 15.8787 16.3624 15.8787C16.6228 15.8787 16.8725 15.9831 17.0561 16.1689L19.6172 18.2181C19.9861 18.5957 20.0999 19.1563 19.9078 19.6492C19.7157 20.1422 19.2535 20.4754 18.7279 20.5L18.6745 20.4553Z"
            fill={activeLink === "/citizensearch" ? "#FFFFFF" : "#003461"}
          />
        </svg>
      ),
      disabled: false, //change this depending
      labelStyle: { color: "#A0A0A0" },
    },

    // {
    //   to: "/partnergranttracker",
    //   label: "Grant Tracker",
    //   svg: (
    //     <svg
    //       xmlns="http://www.w3.org/2000/svg"
    //       width="20"
    //       height="21"
    //       viewBox="0 0 20 21"
    //       fill="none"
    //     >
    //       <path
    //         fill-rule="evenodd"
    //         clip-rule="evenodd"
    //         d="M13.2428 3.23756C13.2428 5.45855 15.0459 7.25902 17.2702 7.25902C17.5151 7.25782 17.7594 7.23431 18 7.18878V15.1615C18 18.5156 16.0215 20.5 12.6624 20.5H5.34636C1.97851 20.5 0 18.5156 0 15.1615V7.8561C0 4.50195 1.97851 2.5 5.34636 2.5H13.3131C13.2659 2.743 13.2423 2.99001 13.2428 3.23756ZM11.15 13.3966L14.0078 9.70878V9.69122C14.2525 9.36247 14.1901 8.89889 13.8671 8.64634C13.7108 8.52568 13.5122 8.47345 13.3167 8.50159C13.1211 8.52972 12.9453 8.6358 12.8295 8.79561L10.4201 11.8951L7.6766 9.73512C7.51997 9.61309 7.32071 9.55922 7.12381 9.58565C6.92691 9.61208 6.74898 9.7166 6.63019 9.87561L3.67562 13.6863C3.57177 13.8158 3.51586 13.9771 3.51734 14.1429C3.5002 14.4781 3.71187 14.7826 4.03238 14.8838C4.35288 14.985 4.70138 14.8573 4.88031 14.5732L7.35125 11.3771L10.0948 13.5283C10.2508 13.6541 10.4514 13.7111 10.6504 13.6863C10.8494 13.6615 11.0297 13.5569 11.15 13.3966Z"
    //         fill={activeLink === "/partnergranttracker" ? "#FFFFFF" : "#003461"}
    //       />

    //       <circle
    //         opacity="0.4"
    //         cx="17.5"
    //         cy="3"
    //         r="2.5"
    //         fill={activeLink === "/partnergranttracker" ? "#FFFFFF" : "#003461"}
    //       />
    //     </svg>
    //   ),
    //   disabled: false, //change this depending
    // },
    {
      to: "/calendar",
      label: "Calendar",
      svg: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="25"
          height="25"
          viewBox="0 0 25 25"
          fill="none"
        >
          <path
            d="M7.44 2.5C7.856 2.5 8.193 2.824 8.193 3.224V4.684C8.861 4.672 9.61 4.672 10.453 4.672H14.468C15.31 4.672 16.059 4.672 16.727 4.685V3.225C16.727 2.825 17.064 2.5 17.48 2.5C17.896 2.5 18.233 2.824 18.233 3.224V4.75C19.678 4.861 20.627 5.134 21.323 5.805C22.021 6.475 22.305 7.387 22.42 8.777L22.5 9.5H2.5V8.776C2.616 7.386 2.9 6.474 3.597 5.804C4.294 5.134 5.242 4.86 6.687 4.749V3.224C6.687 2.824 7.024 2.5 7.44 2.5Z"
            fill={activeLink === "/calendar" ? "#FFFFFF" : "#003461"}
          />
          <path
            opacity="0.5"
            d="M22.5003 14.5V12.5C22.5003 11.661 22.4963 10.165 22.4833 9.5H2.5103C2.4973 10.165 2.5003 11.661 2.5003 12.5V14.5C2.5003 18.271 2.5003 20.157 3.6723 21.328C4.8433 22.5 6.7283 22.5 10.5003 22.5H14.5003C18.2703 22.5 20.1563 22.5 21.3283 21.328C22.5003 20.158 22.5003 18.272 22.5003 14.5Z"
            fill={activeLink === "/calendar" ? "#FFFFFF" : "#003461"}
          />
          <path
            d="M18.5 17.5C18.5 17.7652 18.3946 18.0196 18.2071 18.2071C18.0196 18.3946 17.7652 18.5 17.5 18.5C17.2348 18.5 16.9804 18.3946 16.7929 18.2071C16.6054 18.0196 16.5 17.7652 16.5 17.5C16.5 17.2348 16.6054 16.9804 16.7929 16.7929C16.9804 16.6054 17.2348 16.5 17.5 16.5C17.7652 16.5 18.0196 16.6054 18.2071 16.7929C18.3946 16.9804 18.5 17.2348 18.5 17.5ZM18.5 13.5C18.5 13.7652 18.3946 14.0196 18.2071 14.2071C18.0196 14.3946 17.7652 14.5 17.5 14.5C17.2348 14.5 16.9804 14.3946 16.7929 14.2071C16.6054 14.0196 16.5 13.7652 16.5 13.5C16.5 13.2348 16.6054 12.9804 16.7929 12.7929C16.9804 12.6054 17.2348 12.5 17.5 12.5C17.7652 12.5 18.0196 12.6054 18.2071 12.7929C18.3946 12.9804 18.5 13.2348 18.5 13.5ZM13.5 17.5C13.5 17.7652 13.3946 18.0196 13.2071 18.2071C13.0196 18.3946 12.7652 18.5 12.5 18.5C12.2348 18.5 11.9804 18.3946 11.7929 18.2071C11.6054 18.0196 11.5 17.7652 11.5 17.5C11.5 17.2348 11.6054 16.9804 11.7929 16.7929C11.9804 16.6054 12.2348 16.5 12.5 16.5C12.7652 16.5 13.0196 16.6054 13.2071 16.7929C13.3946 16.9804 13.5 17.2348 13.5 17.5ZM13.5 13.5C13.5 13.7652 13.3946 14.0196 13.2071 14.2071C13.0196 14.3946 12.7652 14.5 12.5 14.5C12.2348 14.5 11.9804 14.3946 11.7929 14.2071C11.6054 14.0196 11.5 13.7652 11.5 13.5C11.5 13.2348 11.6054 12.9804 11.7929 12.7929C11.9804 12.6054 12.2348 12.5 12.5 12.5C12.7652 12.5 13.0196 12.6054 13.2071 12.7929C13.3946 12.9804 13.5 13.2348 13.5 13.5ZM8.5 17.5C8.5 17.7652 8.39464 18.0196 8.20711 18.2071C8.01957 18.3946 7.76522 18.5 7.5 18.5C7.23478 18.5 6.98043 18.3946 6.79289 18.2071C6.60536 18.0196 6.5 17.7652 6.5 17.5C6.5 17.2348 6.60536 16.9804 6.79289 16.7929C6.98043 16.6054 7.23478 16.5 7.5 16.5C7.76522 16.5 8.01957 16.6054 8.20711 16.7929C8.39464 16.9804 8.5 17.2348 8.5 17.5ZM8.5 13.5C8.5 13.7652 8.39464 14.0196 8.20711 14.2071C8.01957 14.3946 7.76522 14.5 7.5 14.5C7.23478 14.5 6.98043 14.3946 6.79289 14.2071C6.60536 14.0196 6.5 13.7652 6.5 13.5C6.5 13.2348 6.60536 12.9804 6.79289 12.7929C6.98043 12.6054 7.23478 12.5 7.5 12.5C7.76522 12.5 8.01957 12.6054 8.20711 12.7929C8.39464 12.9804 8.5 13.2348 8.5 13.5Z"
            fill={activeLink === "/calendar" ? "#FFFFFF" : "#003461"}
          />
        </svg>
      ),
    },
    {
      to: "/message-center",
      label: "Message Center",
      svg: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="25"
          height="25"
          viewBox="0 0 25 25"
          fill="none"
        >
          <path
            d="M21.5 12.5C21.5 7.25 17.175 3 12 3C6.825 3 2.5 7.25 2.5 12.5C2.5 14.95 3.375 17.175 4.85 18.925C4.725 20.025 4.25 21.575 3.5 22.5C3.5 22.5 6.425 22.075 8.5 20.75C9.575 21.125 10.75 21.375 12 21.375C17.175 21.375 21.5 17.75 21.5 12.5Z"
            fill={activeLink === "/message-center" ? "#FFFFFF" : "#003461"}
          />
          <path
            d="M8 12.5C8 13.0523 7.55228 13.5 7 13.5C6.44772 13.5 6 13.0523 6 12.5C6 11.9477 6.44772 11.5 7 11.5C7.55228 11.5 8 11.9477 8 12.5Z"
            fill={activeLink === "/message-center" ? "#003461" : "#FFFFFF"}
          />
          <path
            d="M13 12.5C13 13.0523 12.5523 13.5 12 13.5C11.4477 13.5 11 13.0523 11 12.5C11 11.9477 11.4477 11.5 12 11.5C12.5523 11.5 13 11.9477 13 12.5Z"
            fill={activeLink === "/message-center" ? "#003461" : "#FFFFFF"}
          />
          <path
            d="M18 12.5C18 13.0523 17.5523 13.5 17 13.5C16.4477 13.5 16 13.0523 16 12.5C16 11.9477 16.4477 11.5 17 11.5C17.5523 11.5 18 11.9477 18 12.5Z"
            fill={activeLink === "/message-center" ? "#003461" : "#FFFFFF"}
          />
        </svg>
      ),
    },
    // {
    //   to: "/partnerhistory",
    //   label: "History",
    //   svg: (
    //     <svg
    //       xmlns="http://www.w3.org/2000/svg"
    //       width="25"
    //       height="25"
    //       viewBox="0 0 25 25"
    //       fill="none"
    //     >
    //       <path
    //         fill-rule="evenodd"
    //         clip-rule="evenodd"
    //         d="M5.57922 5.56902C9.37422 1.77902 15.5442 1.81902 19.3622 5.63802C23.1822 9.45802 23.2222 15.631 19.4262 19.426C15.6312 23.221 9.45822 23.182 5.63822 19.362C4.56372 18.292 3.75187 16.9874 3.2664 15.5508C2.78093 14.1141 2.63505 12.5845 2.84022 11.082C2.86714 10.8848 2.97129 10.7064 3.12976 10.586C3.28823 10.4656 3.48803 10.4131 3.68522 10.44C3.88241 10.4669 4.06083 10.5711 4.18123 10.7296C4.30163 10.888 4.35414 11.0878 4.32722 11.285C4.15306 12.5583 4.27653 13.8547 4.68793 15.0722C5.09933 16.2898 5.78745 17.3953 6.69822 18.302C9.94322 21.546 15.1662 21.565 18.3662 18.366C21.5652 15.166 21.5462 9.94302 18.3022 6.69802C15.0592 3.45602 9.83922 3.43502 6.63922 6.63002L7.38722 6.63302C7.48571 6.63348 7.58315 6.65334 7.67397 6.69145C7.76479 6.72957 7.84721 6.7852 7.91653 6.85517C7.98585 6.92514 8.04071 7.00807 8.07797 7.09924C8.11524 7.19041 8.13418 7.28803 8.13372 7.38652C8.13326 7.48501 8.11341 7.58245 8.07529 7.67327C8.03718 7.76409 7.98155 7.84651 7.91158 7.91583C7.84161 7.98515 7.75867 8.04 7.6675 8.07727C7.57633 8.11454 7.47871 8.13348 7.38022 8.13302L4.83422 8.12102C4.63652 8.11997 4.44724 8.0409 4.30753 7.90101C4.16783 7.76112 4.08901 7.57172 4.08822 7.37402L4.07522 4.83002C4.0747 4.73153 4.09358 4.6339 4.13078 4.5427C4.16799 4.45151 4.22279 4.36854 4.29206 4.29852C4.36134 4.2285 4.44372 4.17282 4.53452 4.13464C4.62531 4.09647 4.72273 4.07655 4.82122 4.07602C4.91971 4.0755 5.01735 4.09438 5.10854 4.13158C5.19974 4.16879 5.28271 4.22359 5.35273 4.29286C5.42274 4.36214 5.47843 4.44452 5.5166 4.53531C5.55478 4.62611 5.5747 4.72353 5.57522 4.82202L5.57922 5.56902Z"
    //         fill={activeLink === "/partnerhistory" ? "#FFFFFF" : "#348BFF"}
    //       />
    //       <path
    //         opacity="0.5"
    //         d="M12.5 7.75C12.6989 7.75 12.8897 7.82902 13.0303 7.96967C13.171 8.11032 13.25 8.30109 13.25 8.5V12.19L15.53 14.47C15.6037 14.5387 15.6628 14.6215 15.7038 14.7135C15.7448 14.8055 15.7668 14.9048 15.7686 15.0055C15.7704 15.1062 15.7518 15.2062 15.7141 15.2996C15.6764 15.393 15.6203 15.4778 15.549 15.549C15.4778 15.6203 15.393 15.6764 15.2996 15.7141C15.2062 15.7518 15.1062 15.7704 15.0055 15.7686C14.9048 15.7668 14.8055 15.7448 14.7135 15.7038C14.6215 15.6628 14.5387 15.6037 14.47 15.53L12.043 13.104C11.95 13.011 11.8763 12.9007 11.826 12.7792C11.7757 12.6577 11.7499 12.5275 11.75 12.396V8.5C11.75 8.30109 11.829 8.11032 11.9697 7.96967C12.1103 7.82902 12.3011 7.75 12.5 7.75Z"
    //         fill={activeLink === "/partnerhistory" ? "#FFFFFF" : "#348BFF"}
    //       />
    //     </svg>
    //   ),
    // },
    // {
    //   to: "/partnersettings",
    //   label: "Settings",
    //   svg: (
    //     <svg
    //       xmlns="http://www.w3.org/2000/svg"
    //       width="24"
    //       height="24"
    //       viewBox="0 0 24 24"
    //       fill="none"
    //     >
    //       <path
    //         d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z"
    //         stroke={activeLink === "/partnersettings" ? "#FFFFFF" : "#348BFF"}
    //         strokeWidth="2"
    //         strokeLinecap="round"
    //         strokeLinejoin="round"
    //       />
    //       <path
    //         d="M19.4 15C19.2669 15.3016 19.2272 15.6362 19.286 15.9606C19.3448 16.285 19.4995 16.5843 19.73 16.82L19.79 16.88C19.976 17.0657 20.1235 17.2863 20.2241 17.5291C20.3248 17.7719 20.3766 18.0322 20.3766 18.295C20.3766 18.5578 20.3248 18.8181 20.2241 19.0609C20.1235 19.3037 19.976 19.5243 19.79 19.71C19.6043 19.896 19.3837 20.0435 19.1409 20.1441C18.8981 20.2448 18.6378 20.2966 18.375 20.2966C18.1122 20.2966 17.8519 20.2448 17.6091 20.1441C17.3663 20.0435 17.1457 19.896 16.96 19.71L16.9 19.65C16.6643 19.4195 16.365 19.2648 16.0406 19.206C15.7162 19.1472 15.3816 19.1869 15.08 19.32C14.7842 19.4468 14.532 19.6572 14.3543 19.9255C14.1766 20.1938 14.0813 20.5082 14.08 20.83V21C14.08 21.5304 13.8693 22.0391 13.4942 22.4142C13.1191 22.7893 12.6104 23 12.08 23C11.5496 23 11.0409 22.7893 10.6658 22.4142C10.2907 22.0391 10.08 21.5304 10.08 21V20.91C10.0723 20.579 9.96512 20.258 9.77251 19.9887C9.5799 19.7194 9.31074 19.5143 9 19.4C8.69838 19.2669 8.36381 19.2272 8.03941 19.286C7.71502 19.3448 7.41568 19.4995 7.18 19.73L7.12 19.79C6.93425 19.976 6.71368 20.1235 6.47088 20.2241C6.22808 20.3248 5.96783 20.3766 5.705 20.3766C5.44217 20.3766 5.18192 20.3248 4.93912 20.2241C4.69632 20.1235 4.47575 19.976 4.29 19.79C4.10405 19.6043 3.95653 19.3837 3.85588 19.1409C3.75523 18.8981 3.70343 18.6378 3.70343 18.375C3.70343 18.1122 3.75523 17.8519 3.85588 17.6091C3.95653 17.3663 4.10405 17.1457 4.29 16.96L4.35 16.9C4.58054 16.6643 4.73519 16.365 4.794 16.0406C4.85282 15.7162 4.81312 15.3816 4.68 15.08C4.55324 14.7842 4.34276 14.532 4.07447 14.3543C3.80618 14.1766 3.49179 14.0813 3.17 14.08H3C2.46957 14.08 1.96086 13.8693 1.58579 13.4942C1.21071 13.1191 1 12.6104 1 12.08C1 11.5496 1.21071 11.0409 1.58579 10.6658C1.96086 10.2907 2.46957 10.08 3 10.08H3.09C3.42099 10.0723 3.742 9.96512 4.0113 9.77251C4.28059 9.5799 4.48572 9.31074 4.6 9C4.73312 8.69838 4.77282 8.36381 4.714 8.03941C4.65519 7.71502 4.50054 7.41568 4.27 7.18L4.21 7.12C4.02405 6.93425 3.87653 6.71368 3.77588 6.47088C3.67523 6.22808 3.62343 5.96783 3.62343 5.705C3.62343 5.44217 3.67523 5.18192 3.77588 4.93912C3.87653 4.69632 4.02405 4.47575 4.21 4.29C4.39575 4.10405 4.61632 3.95653 4.85912 3.85588C5.10192 3.75523 5.36217 3.70343 5.625 3.70343C5.88783 3.70343 6.14808 3.75523 6.39088 3.85588C6.63368 3.95653 6.85425 4.10405 7.04 4.29L7.1 4.35C7.33568 4.58054 7.63502 4.73519 7.95941 4.794C8.28381 4.85282 8.61838 4.81312 8.92 4.68H9C9.29577 4.55324 9.54802 4.34276 9.72569 4.07447C9.90337 3.80618 9.99872 3.49179 10 3.17V3C10 2.46957 10.2107 1.96086 10.5858 1.58579C10.9609 1.21071 11.4696 1 12 1C12.5304 1 13.0391 1.21071 13.4142 1.58579C13.7893 1.96086 14 2.46957 14 3V3.09C14.0013 3.41179 14.0966 3.72618 14.2743 3.99447C14.452 4.26276 14.7042 4.47324 15 4.6C15.3016 4.73312 15.6362 4.77282 15.9606 4.714C16.285 4.65519 16.5843 4.50054 16.82 4.27L16.88 4.21C17.0657 4.02405 17.2863 3.87653 17.5291 3.77588C17.7719 3.67523 18.0322 3.62343 18.295 3.62343C18.5578 3.62343 18.8181 3.67523 19.0609 3.77588C19.3037 3.87653 19.5243 4.02405 19.71 4.21C19.896 4.39575 20.0435 4.61632 20.1441 4.85912C20.2448 5.10192 20.2966 5.36217 20.2966 5.625C20.2966 5.88783 20.2448 6.14808 20.1441 6.39088C20.0435 6.63368 19.896 6.85425 19.71 7.04L19.65 7.1C19.4195 7.33568 19.2648 7.63502 19.206 7.95941C19.1472 8.28381 19.1869 8.61838 19.32 8.92V9C19.4468 9.29577 19.6572 9.54802 19.9255 9.72569C20.1938 9.90337 20.5082 9.99872 20.83 10H21C21.5304 10 22.0391 10.2107 22.4142 10.5858C22.7893 10.9609 23 11.4696 23 12C23 12.5304 22.7893 13.0391 22.4142 13.4142C22.0391 13.7893 21.5304 14 21 14H20.91C20.5882 14.0013 20.2738 14.0966 20.0055 14.2743C19.7372 14.452 19.5268 14.7042 19.4 15Z"
    //         stroke={activeLink === "/partnersettings" ? "#FFFFFF" : "#348BFF"}
    //         strokeWidth="2"
    //         strokeLinecap="round"
    //         strokeLinejoin="round"
    //       />
    //     </svg>
    //   ),
    // },
    // {
    //   to: "/settings",
    //   label: "Settings",
    //   svg: (
    //     <svg
    //       xmlns="http://www.w3.org/2000/svg"
    //       width="20"
    //       height="21"
    //       viewBox="0 0 20 21"
    //       fill={activeLink === "/settings" ? "#FFFFFF" : "#348BFF"}
    //     >
    //       <path
    //         d="M10.0117 13.3302C8.40726 13.3302 7.10938 12.0802 7.10938 10.5102C7.10938 8.94018 8.40726 7.68018 10.0117 7.68018C11.6162 7.68018 12.8834 8.94018 12.8834 10.5102C12.8834 12.0802 11.6162 13.3302 10.0117 13.3302Z"
    //         fill={activeLink === "/settings" ? "#FFFFFF" : "#348BFF"}
    //       />
    //       <path
    //         opacity="0.4"
    //         d="M19.2301 12.8701C19.036 12.5701 18.76 12.2701 18.4023 12.0801C18.1162 11.9401 17.9322 11.7101 17.7687 11.4401C17.2475 10.5801 17.5541 9.45012 18.4228 8.94012C19.4447 8.37012 19.7718 7.10012 19.179 6.11012L18.4943 4.93012C17.9118 3.94012 16.6344 3.59012 15.6226 4.17012C14.7233 4.65012 13.5685 4.33012 13.0473 3.48012C12.8838 3.20012 12.7918 2.90012 12.8122 2.60012C12.8429 2.21012 12.7203 1.84012 12.5363 1.54012C12.1582 0.920122 11.4735 0.500122 10.7172 0.500122H9.27627C8.53024 0.520122 7.84553 0.920122 7.4674 1.54012C7.27323 1.84012 7.16081 2.21012 7.18125 2.60012C7.20169 2.90012 7.10972 3.20012 6.9462 3.48012C6.425 4.33012 5.27019 4.65012 4.38109 4.17012C3.35913 3.59012 2.09191 3.94012 1.49917 4.93012L0.814459 6.11012C0.231943 7.10012 0.55897 8.37012 1.57071 8.94012C2.43937 9.45012 2.74596 10.5801 2.23498 11.4401C2.06125 11.7101 1.87729 11.9401 1.59115 12.0801C1.24368 12.2701 0.937094 12.5701 0.773581 12.8701C0.395456 13.4901 0.415896 14.2701 0.79402 14.9201L1.49917 16.1201C1.87729 16.7601 2.58245 17.1601 3.31825 17.1601C3.66572 17.1601 4.0745 17.0601 4.40153 16.8601C4.65702 16.6901 4.96361 16.6301 5.30085 16.6301C6.31259 16.6301 7.16081 17.4601 7.18125 18.4501C7.18125 19.6001 8.12145 20.5001 9.30692 20.5001H10.6968C11.872 20.5001 12.8122 19.6001 12.8122 18.4501C12.8429 17.4601 13.6911 16.6301 14.7029 16.6301C15.0299 16.6301 15.3365 16.6901 15.6022 16.8601C15.9292 17.0601 16.3278 17.1601 16.6855 17.1601C17.411 17.1601 18.1162 16.7601 18.4943 16.1201L19.2097 14.9201C19.5776 14.2501 19.6083 13.4901 19.2301 12.8701Z"
    //         fill={activeLink === "/settings" ? "#FFFFFF" : "#348BFF"}
    //       />
    //     </svg>
    //   ),
    // },
    {
      to: "/myhistory",
      label: "History",
      svg: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="25"
          height="25"
          viewBox="0 0 25 25"
          fill="none"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M5.57922 5.56902C9.37422 1.77902 15.5442 1.81902 19.3622 5.63802C23.1822 9.45802 23.2222 15.631 19.4262 19.426C15.6312 23.221 9.45822 23.182 5.63822 19.362C4.56372 18.292 3.75187 16.9874 3.2664 15.5508C2.78093 14.1141 2.63505 12.5845 2.84022 11.082C2.86714 10.8848 2.97129 10.7064 3.12976 10.586C3.28823 10.4656 3.48803 10.4131 3.68522 10.44C3.88241 10.4669 4.06083 10.5711 4.18123 10.7296C4.30163 10.888 4.35414 11.0878 4.32722 11.285C4.15306 12.5583 4.27653 13.8547 4.68793 15.0722C5.09933 16.2898 5.78745 17.3953 6.69822 18.302C9.94322 21.546 15.1662 21.565 18.3662 18.366C21.5652 15.166 21.5462 9.94302 18.3022 6.69802C15.0592 3.45602 9.83922 3.43502 6.63922 6.63002L7.38722 6.63302C7.48571 6.63348 7.58315 6.65334 7.67397 6.69145C7.76479 6.72957 7.84721 6.7852 7.91653 6.85517C7.98585 6.92514 8.04071 7.00807 8.07797 7.09924C8.11524 7.19041 8.13418 7.28803 8.13372 7.38652C8.13326 7.48501 8.11341 7.58245 8.07529 7.67327C8.03718 7.76409 7.98155 7.84651 7.91158 7.91583C7.84161 7.98515 7.75867 8.04 7.6675 8.07727C7.57633 8.11454 7.47871 8.13348 7.38022 8.13302L4.83422 8.12102C4.63652 8.11997 4.44724 8.0409 4.30753 7.90101C4.16783 7.76112 4.08901 7.57172 4.08822 7.37402L4.07522 4.83002C4.0747 4.73153 4.09358 4.6339 4.13078 4.5427C4.16799 4.45151 4.22279 4.36854 4.29206 4.29852C4.36134 4.2285 4.44372 4.17282 4.53452 4.13464C4.62531 4.09647 4.72273 4.07655 4.82122 4.07602C4.91971 4.0755 5.01735 4.09438 5.10854 4.13158C5.19974 4.16879 5.28271 4.22359 5.35273 4.29286C5.42274 4.36214 5.47843 4.44452 5.5166 4.53531C5.55478 4.62611 5.5747 4.72353 5.57522 4.82202L5.57922 5.56902Z"
            fill={activeLink === "/myhistory" ? "#FFFFFF" : "#003461"}
          />
          <path
            opacity="0.5"
            d="M12.5 7.75C12.6989 7.75 12.8897 7.82902 13.0303 7.96967C13.171 8.11032 13.25 8.30109 13.25 8.5V12.19L15.53 14.47C15.6037 14.5387 15.6628 14.6215 15.7038 14.7135C15.7448 14.8055 15.7668 14.9048 15.7686 15.0055C15.7704 15.1062 15.7518 15.2062 15.7141 15.2996C15.6764 15.393 15.6203 15.4778 15.549 15.549C15.4778 15.6203 15.393 15.6764 15.2996 15.7141C15.2062 15.7518 15.1062 15.7704 15.0055 15.7686C14.9048 15.7668 14.8055 15.7448 14.7135 15.7038C14.6215 15.6628 14.5387 15.6037 14.47 15.53L12.043 13.104C11.95 13.011 11.8763 12.9007 11.826 12.7792C11.7757 12.6577 11.7499 12.5275 11.75 12.396V8.5C11.75 8.30109 11.829 8.11032 11.9697 7.96967C12.1103 7.82902 12.3011 7.75 12.5 7.75Z"
            fill={activeLink === "/myhistory" ? "#FFFFFF" : "#003461"}
          />
        </svg>
      ),
    },
    // {
    //   to: "/admindisasterlockbox",
    //   label: "Admin Disaster Lockbox",
    //   svg: (
    //     <svg
    //       xmlns="http://www.w3.org/2000/svg"
    //       width="25"
    //       height="25"
    //       viewBox="0 0 25 25"
    //       fill="none"
    //     >
    //       <path
    //         d="M12.5 2.5C6.977 2.5 2.5 6.977 2.5 12.5C2.5 18.023 6.977 22.5 12.5 22.5C18.023 22.5 22.5 18.023 22.5 12.5C22.5 6.977 18.023 2.5 12.5 2.5Z"
    //         fill={activeLink === "/admindisasterlockbox" ? "#FFFFFF" : "#003461"}
    //         opacity="0.4"
    //       />
    //       <path
    //         d="M12.5 6.5C13.8807 6.5 15 7.61929 15 9C15 10.3807 13.8807 11.5 12.5 11.5C11.1193 11.5 10 10.3807 10 9C10 7.61929 11.1193 6.5 12.5 6.5Z"
    //         fill={activeLink === "/admindisasterlockbox" ? "#FFFFFF" : "#003461"}
    //       />
    //       <path
    //         d="M17.5 16.5C17.5 18.433 15.261 20 12.5 20C9.739 20 7.5 18.433 7.5 16.5C7.5 14.567 9.739 13 12.5 13C15.261 13 17.5 14.567 17.5 16.5Z"
    //         fill={activeLink === "/admindisasterlockbox" ? "#FFFFFF" : "#003461"}
    //       />
    //     </svg>
    //   ),
    // },
    // {
    //   to: "/adminsurveyquestions",
    //   label: "Admin Survey Questions",
    //   svg: (
    //     <svg
    //       xmlns="http://www.w3.org/2000/svg"
    //       width="25"
    //       height="25"
    //       viewBox="0 0 25 25"
    //       fill="none"
    //     >
    //       <path
    //         d="M12.5 2.5C6.977 2.5 2.5 6.977 2.5 12.5C2.5 18.023 6.977 22.5 12.5 22.5C18.023 22.5 22.5 18.023 22.5 12.5C22.5 6.977 18.023 2.5 12.5 2.5Z"
    //         fill={activeLink === "/adminsurveyquestions" ? "#FFFFFF" : "#003461"}
    //         opacity="0.4"
    //       />
    //       <path
    //         d="M12.5 6.5C13.8807 6.5 15 7.61929 15 9C15 10.3807 13.8807 11.5 12.5 11.5C11.1193 11.5 10 10.3807 10 9C10 7.61929 11.1193 6.5 12.5 6.5Z"
    //         fill={activeLink === "/adminsurveyquestions" ? "#FFFFFF" : "#003461"}
    //       />
    //       <path
    //         d="M17.5 16.5C17.5 18.433 15.261 20 12.5 20C9.739 20 7.5 18.433 7.5 16.5C7.5 14.567 9.739 13 12.5 13C15.261 13 17.5 14.567 17.5 16.5Z"
    //         fill={activeLink === "/adminsurveyquestions" ? "#FFFFFF" : "#003461"}
    //       />
    //     </svg>
    //   ),
    // },
  ];

  if (data && role === "citizen") {
    links = links.filter(
      (link) =>
        link.to !== "/partneropportunities" &&
        link.to !== "/granttracker" &&
        link.to !== "/database" &&
        link.to !== "/cenmyrecoveryplan" &&
        link.to !== "/settings" &&
        link.to !== "/reportgenerator" &&
        link.to !== "/centerpartnerships" &&
        link.to !== "/centerprofile" &&
        link.to !== "/granttracker" &&
        link.to !== "/partnergranttracker" &&
        link.to !== "/myhistory" &&
        link.to !== "/partnerhistory" &&
        link.to !== "/citizensearch" &&
        link.to !== "/center" &&
        link.to !== "/npcpartner" &&
        link.to !== "/drpartner" &&
        link.to !== "/citizen" &&
        link.to !== "/partnerlockbox" &&
        link.to !== "/centerlockbox" &&
        link.to !== "/partnerprofile" &&
        link.to !== "/partnersettings" &&
        link.to !== "/members" &&
        link.to !== "/partnerships" &&
        link.to !== "/employees" &&
        link.to !== "/casemanagement" &&
        link.to !== "/disasterdeclaration" &&
        link.to !== "/message-center" &&
        link.to !== "/demographics"
    );
  } else if (center && data && role === "center") {
    // First filter to only keep center-specific routes
    links = links.filter(
      (link) =>
        link.to === "/home" ||
        link.to === "/library" ||
        link.to === "/library/certificationvideos" ||
        link.to === "/library/communitycontacts" ||
        link.to === "/library/localnonprofit" ||
        link.to === "/library/quickInfo" ||
        link.to === "/library/prepareness" ||
        link.to === "/library/response" ||
        link.to === "/library/recovery" ||
        link.to === "/centerprofile" ||
        link.to === "/database" ||
        link.to === "/cenmyrecoveryplan" ||
        link.to === "/reportgenerator" ||
        link.to === "/granttracker" ||
        link.to === "/communication" ||
        link.to === "/calendar" ||
        link.to === "/message-center" ||
        link.to === "/members" ||
        link.to === "/partnerships" ||
        link.to === "/employees" ||
        link.to === "/casemanagement" ||
        link.to === "/disasterdeclaration" ||
        link.to === "/map" ||
        link.to === "/centerlockbox"
    );

    // Then check if center is not approved
    if (!center.is_certified) {
      // Modify all routes except library, centerprofile, and home to be disabled
      links = links.map((link) => {
        // Allow access only to library, centerprofile, and home
        if (
          link.to === "/library" ||
          link.to === "/library/certificationvideos" ||
          link.to === "/library/communitycontacts" ||
          link.to === "/library/localnonprofit" ||
          link.to === "/library/quickInfo" ||
          link.to === "/library/prepareness" ||
          link.to === "/library/response" ||
          link.to === "/library/recovery" ||
          link.to === "/centerprofile"
        ) {
          return link;
        }

        // Clone the SVG with modified colors for disabled state
        // const modifiedSvg = React.cloneElement(link.svg, {
        //   children: React.Children.map(link.svg.props.children, (child) => {
        //     if (
        //       child.type === "path" ||
        //       child.type === "circle" ||
        //       child.type === "ellipse" ||
        //       child.type === "rect"
        //     ) {
        //       return React.cloneElement(child, {
        //         fill: "#A0A0A0",
        //         stroke: child.props.stroke ? "#A0A0A0" : undefined,
        //         opacity: child.props.opacity || 1,
        //       });
        //     }
        //     return child;
        //   }),
        // });

        // Return disabled version of the link
        return {
          ...link,
          disabled: true,
          // svg: modifiedSvg,
          // labelStyle: { color: "#A0A0A0" },
        };
      });
    }

    // Keep the Dashboard label change for both approved and unapproved
    links = links.map((link) => {
      if (link.to === "/home") {
        return { ...link, label: "Dashboard" };
      }
      return link;
    });
  } else if (data && role === "partner") {
    // First filter to only keep partner-specific routes
    links = links.filter(
      (link) =>
        link.to === "/home" ||
        link.to === "/reportgenerator" ||
        link.to === "/library" ||
        link.to === "/library/certificationvideos" ||
        link.to === "/library/communitycontacts" ||
        link.to === "/library/localnonprofit" ||
        link.to === "/library/quickInfo" ||
        link.to === "/library/prepareness" ||
        link.to === "/library/response" ||
        link.to === "/library/recovery" ||
        link.to === "/partnerprofile" ||
        link.to === "/partneropportunities" ||
        link.to === "/partnercitizensearch" ||
        link.to === "/partnerlockbox" ||
        link.to === "/calendar" ||
        link.to === "/citizensearch" ||
        link.to === "/employees" ||
        link.to === "/map" ||
        link.to === "/centerpartnerships" ||
        link.to === "/communication" ||
        link.to === "/demographics"
    );

    // Then check if partner is not approved
    if (!data.is_certified) {
      // Modify all routes except library, partnerprofile, home, and Center Partnerships
      links = links.map((link) => {
        // Allow access to these routes even when not certified
        if (
          link.to === "/partnerprofile" ||
          link.to === "/library" ||
          link.to === "/library/certificationvideos" ||
          link.to === "/library/communitycontacts" ||
          link.to === "/library/localnonprofit" ||
          link.to === "/library/quickInfo" ||
          link.to === "/library/prepareness" ||
          link.to === "/library/response" ||
          link.to === "/library/recovery" ||
          link.to === "/centerpartnerships"
        ) {
          return link;
        }

        // Return disabled version of the link
        return {
          ...link,
          disabled: true,
        };
      });
    }

    // Keep the Dashboard label change for both approved and unapproved
    links = links.map((link) => {
      if (link.to === "/home") {
        return { ...link, label: "Dashboard" };
      }
      return link;
    });
  } else if (data && data.user_role === "admin") {
    links = links.filter(
      (link) =>
        link.to === "/home" ||
        link.to === "/employees" ||
        link.to === "/communication" ||
        link.to === "/library" ||
        link.to === "/library/certificationvideos" ||
        link.to === "/library/communitycontacts" ||
        link.to === "/library/localnonprofit" ||
        link.to === "/map" ||
        link.to === "/message-center" ||
        link.to === "/calendar" ||
        link.to === "/center" ||
        link.to === "/adminlockbox" ||
        link.to === "/adminsurveyquestions" ||
        link.to === "/admindisasterlockbox" ||
        link.to === "/adminsurveyquestions"
    );
    // Add disabled property and modify colors for all links except center
    links = links.map((link) => {
      // Change "Home" to "Dashboard"
      if (link.to === "/home") {
        return {
          ...link,
          label: "Dashboard",
          disabled: true,
          svg: React.cloneElement(link.svg, {
            children: React.Children.map(link.svg.props.children, (child) => {
              if (
                child.type === "path" ||
                child.type === "circle" ||
                child.type === "ellipse" ||
                child.type === "rect"
              ) {
                return React.cloneElement(child, {
                  fill: "#A0A0A0",
                  stroke: child.props.stroke ? "#A0A0A0" : undefined,
                  opacity: child.props.opacity || 1,
                });
              }
              return child;
            }),
          }),
          labelStyle: { color: "#A0A0A0" },
        };
      }

      if (link.to === "/center" || link.to === "/employees") {
        return link;
      }

      // Clone the SVG with modified colors for disabled state
      const modifiedSvg = React.cloneElement(link.svg, {
        children: React.Children.map(link.svg.props.children, (child) => {
          if (
            child.type === "path" ||
            child.type === "circle" ||
            child.type === "ellipse" ||
            child.type === "rect"
          ) {
            return React.cloneElement(child, {
              fill: "#A0A0A0",
              stroke: child.props.stroke ? "#A0A0A0" : undefined,
              opacity: child.props.opacity || 1,
            });
          }
          return child;
        }),
      });

      return {
        ...link,
        disabled: true,
        svg: modifiedSvg,
        labelStyle: { color: "#A0A0A0" },
      };
    });
  }

  const renderSubmenu = (submenu, level = 0) => {
    const isDisasterSubmenu = submenu.to.includes(
      "/citmyrecoveryplan/disasters/"
    );
    const isDisasterTab = submenu.label === "Disasters";
    const isActive = isDisasterSubmenu
      ? activeDisasterTab === submenu.label
      : activeLink.startsWith(submenu.to);

    const handleSubmenuClick = () => {
      if (!isDisasterTab) {
        handleLinkClick(submenu, true);
      }
    };

    const content = (
      <div
        className={`flex flex-row items-start justify-start gap-2 self-stretch px-2.5 py-[5.5px] ${
          isActive ? "text-[#003461]" : ""
        } ${isDisasterTab ? "cursor-default" : "cursor-pointer"}`}
        style={{ marginLeft: `${level * 8}px` }}
        onClick={handleSubmenuClick}
      >
        <div className="flex items-center gap-2">
          {level === 2 ? (
            <div
              className={`h-2 w-2 rounded-full ${
                isActive ? "bg-[#003461]" : "bg-[#A1A1A1]"
              }`}
            />
          ) : (
            <svg
              width="3"
              height="19"
              viewBox="0 0 3 19"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{
                opacity: isActive ? 1 : 0,
              }}
            >
              <rect
                y="0.0888672"
                width="2.92376"
                height="18.8231"
                rx="1"
                fill="#003461"
              />
            </svg>
          )}
          <div className="flex flex-col items-start justify-start">
            <p
              className={`relative min-w-[40px] font-semibold ${
                isActive ? "text-[#003461]" : "text-[#A1A1A1]"
              }`}
            >
              {submenu.label}
            </p>
          </div>
        </div>
      </div>
    );

    return (
      <React.Fragment key={submenu.to}>
        {content}
        {submenu.submenus &&
          (openSubmenus.includes(submenu.to) || isDisasterTab) && (
            <div>
              {submenu.submenus.map((nestedSubmenu) =>
                renderSubmenu(nestedSubmenu, level + 1)
              )}
            </div>
          )}
      </React.Fragment>
    );
  };

  if (loading) {
    return <div className=""></div>;
  }

  return (
    <div>
      <aside className={`overflow-y-auto  ${className}`}>
        <div
          className={`fixed bottom-0 left-0 ${
            hasEmergencyBanner ? "top-[8vh]" : "top-[6vh]"
          } w-64 shadow-md ${className}`}
        >
          <div
            className={`bg-nav-active box-border flex h-[93.2vh] w-[246px] cursor-pointer flex-col items-start justify-between gap-[5px] overflow-y-auto bg-white px-[5px] pb-[40px] pt-[15px] text-left text-xs text-darkgray shadow-[0px_0px_10px_rgba(0,_0,_0,_0.25)] ${className}`}
          >
            <div className="flex w-full flex-col gap-[5px]">
              {links.map((link) => {
                const isActive = isLinkOrSubmenuActive(link, activeLink);
                const showSubmenus = openSubmenus.includes(link.to);

                return (
                  <React.Fragment key={link.to}>
                    <div
                      className={`flex cursor-pointer flex-row items-center justify-between gap-2 self-stretch rounded-[6px] px-2.5 py-[5.5px] no-underline ${
                        isActive ? "bg-header-panel rounded-md text-white" : ""
                      } ${link.disabled ? "cursor-default" : ""}`}
                      onClick={(e) => {
                        if (link.disabled) {
                          setShowBlur(true);
                        } else {
                          navigate(
                            link.submenus ? link.submenus[0].to : link.to
                          );
                          handleLinkClick(link);
                        }
                      }}
                    >
                      <div className="flex w-full items-center justify-between">
                        <div className="flex items-center gap-2">
                          {link.svg}
                          <p
                            className={`relative min-w-[40px] font-semibold ${
                              isActive
                                ? "text-[#FFF]"
                                : grayMenuItems.includes(link.to)
                                ? "text-[#A0A0A0]"
                                : "text-[#003461]"
                            }`}
                          >
                            {link.label}
                          </p>
                        </div>

                        {/* Add vertical dots only for Recovery Tracker */}
                        {/* {link.to === "/citmyrecoveryplan" && (
                          <div
                            className="flex-end ml-2 items-center"
                            onClick={(e) => {
                              e.stopPropagation();
                              const rect =
                                e.currentTarget.getBoundingClientRect();
                              setOptionsPosition({
                                x: rect.right + 5, // 5px offset from the dots
                                y: rect.top,
                              });
                              setShowOptions(!showOptions);
                            }}
                          >
                            <img src={verticalDot} alt="Vertical Dot" />
                          </div>
                        )} */}
                      </div>
                    </div>
                    {link.submenus && showSubmenus && (
                      <div className="ml-4">
                        {link.submenus.map((submenu) =>
                          renderSubmenu(submenu, 1)
                        )}
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            <div>
              {role === "citizen" && (
                <div className="mb-4 mr-2 mt-auto flex flex-col rounded-tr-[100px] bg-[#003461] p-6">
                  <div className="mb-2">
                    <h3 className="text-lg mb-1 font-semibold text-white">
                      Invite Your Friends
                    </h3>
                    <p className="mb-3 text-sm text-white/80">
                      Invite people to DARCI, Be Ready for Anything
                    </p>
                    <button
                      className="rounded-md bg-white px-4 py-2 text-sm font-medium text-[#003461] transition-colors hover:cursor-pointer hover:bg-white/90"
                      onClick={() => {
                        setShowInvite(true);
                      }}
                    >
                      Invite Friends
                    </button>
                  </div>
                </div>
              )}

              <div className="mb-4">
                {(role === "partner" ||
                  (role === "center" && center?.is_certified)) && (
                  <div
                    onClick={() => {
                      const newRoute = "/casemanagement/dashboard/none";
                      const absoluteURL = `${window.location.origin}${newRoute}`;
                      window.open(absoluteURL, "_blank");
                    }}
                    className={`flex cursor-pointer flex-row items-start justify-start gap-2 self-stretch rounded-[6px] px-2.5 py-[5.5px] no-underline ${
                      activeLink === "/casemanagement"
                        ? "bg-header-panel rounded-md text-white"
                        : ""
                    }`}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="25"
                      height="25"
                      viewBox="0 0 25 25"
                      fill="none"
                    >
                      <path
                        opacity="0.5"
                        d="M21.5 16.498V10.498C21.5 7.67 21.5 6.256 20.621 5.377C19.853 4.609 18.675 4.512 16.5 4.5H8.5C6.325 4.512 5.147 4.609 4.379 5.377C3.5 6.256 3.5 7.67 3.5 10.498V16.498C3.5 19.327 3.5 20.741 4.379 21.62C5.257 22.498 6.672 22.498 9.5 22.498H15.5C18.328 22.498 19.743 22.498 20.621 21.62C21.5 20.74 21.5 19.327 21.5 16.498Z"
                        fill={
                          activeLink === "/casemanagement"
                            ? "#FFFFFF"
                            : "#003461"
                        }
                      />
                      <path
                        d="M8.5 4C8.5 3.60218 8.65804 3.22064 8.93934 2.93934C9.22064 2.65804 9.60218 2.5 10 2.5H15C15.3978 2.5 15.7794 2.65804 16.0607 2.93934C16.342 3.22064 16.5 3.60218 16.5 4V5C16.5 5.39782 16.342 5.77936 16.0607 6.06066C15.7794 6.34196 15.3978 6.5 15 6.5H10C9.60218 6.5 9.22064 6.34196 8.93934 6.06066C8.65804 5.77936 8.5 5.39782 8.5 5V4Z"
                        fill={
                          activeLink === "/casemanagement"
                            ? "#FFFFFF"
                            : "#003461"
                        }
                      />
                      <path
                        d="M11.15 13.3966L14.0078 9.70878V9.69122C14.2525 9.36247 14.1901 8.89889 13.8671 8.64634C13.7108 8.52568 13.5122 8.47345 13.3167 8.50159C13.1211 8.52972 12.9453 8.6358 12.8295 8.79561L10.4201 11.8951L7.6766 9.73512C7.51997 9.61309 7.32071 9.55922 7.12381 9.58565C6.92691 9.61208 6.74898 9.7166 6.63019 9.87561L3.67562 13.6863C3.57177 13.8158 3.51586 13.9771 3.51734 14.1429C3.5002 14.4781 3.71187 14.7826 4.03238 14.8838C4.35288 14.985 4.70138 14.8573 4.88031 14.5732L7.35125 11.3771L10.0948 13.5283C10.2508 13.6541 10.4514 13.7111 10.6504 13.6863C10.8494 13.6615 11.0297 13.5569 11.15 13.3966Z"
                        fill={
                          activeLink === "/casemanagement"
                            ? "#FFFFFF"
                            : "#003461"
                        }
                      />
                    </svg>

                    <div className="flex flex-col items-start justify-start px-0 pb-0 pt-[5px]">
                      <p className="relative min-w-[40px] font-semibold text-[#003461]">
                        Case Management
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="mb-4">
                {(role === "partner" ||
                  (role === "center" && center?.is_certified)) && (
                  <div
                    onClick={() => {
                      const newRoute = "/screening/dashboard/none";
                      const absoluteURL = `${window.location.origin}${newRoute}`;
                      window.open(absoluteURL, "_blank");
                    }}
                    className={`flex cursor-pointer flex-row items-start justify-start gap-2 self-stretch rounded-[6px] px-2.5 py-[5.5px] no-underline ${
                      activeLink === "/screening"
                        ? "bg-header-panel rounded-md text-white"
                        : ""
                    }`}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="25"
                      height="25"
                      viewBox="0 0 25 25"
                      fill="none"
                    >
                      <path
                        opacity="0.5"
                        d="M21.5 16.498V10.498C21.5 7.67 21.5 6.256 20.621 5.377C19.853 4.609 18.675 4.512 16.5 4.5H8.5C6.325 4.512 5.147 4.609 4.379 5.377C3.5 6.256 3.5 7.67 3.5 10.498V16.498C3.5 19.327 3.5 20.741 4.379 21.62C5.257 22.498 6.672 22.498 9.5 22.498H15.5C18.328 22.498 19.743 22.498 20.621 21.62C21.5 20.74 21.5 19.327 21.5 16.498Z"
                        fill={
                          activeLink === "/screening" ? "#FFFFFF" : "#003461"
                        }
                      />
                      <path
                        d="M8.5 4C8.5 3.60218 8.65804 3.22064 8.93934 2.93934C9.22064 2.65804 9.60218 2.5 10 2.5H15C15.3978 2.5 15.7794 2.65804 16.0607 2.93934C16.342 3.22064 16.5 3.60218 16.5 4V5C16.5 5.39782 16.342 5.77936 16.0607 6.06066C15.7794 6.34196 15.3978 6.5 15 6.5H10C9.60218 6.5 9.22064 6.34196 8.93934 6.06066C8.65804 5.77936 8.5 5.39782 8.5 5V4Z"
                        fill={
                          activeLink === "/screening" ? "#FFFFFF" : "#003461"
                        }
                      />
                      <path
                        d="M11.15 13.3966L14.0078 9.70878V9.69122C14.2525 9.36247 14.1901 8.89889 13.8671 8.64634C13.7108 8.52568 13.5122 8.47345 13.3167 8.50159C13.1211 8.52972 12.9453 8.6358 12.8295 8.79561L10.4201 11.8951L7.6766 9.73512C7.51997 9.61309 7.32071 9.55922 7.12381 9.58565C6.92691 9.61208 6.74898 9.7166 6.63019 9.87561L3.67562 13.6863C3.57177 13.8158 3.51586 13.9771 3.51734 14.1429C3.5002 14.4781 3.71187 14.7826 4.03238 14.8838C4.35288 14.985 4.70138 14.8573 4.88031 14.5732L7.35125 11.3771L10.0948 13.5283C10.2508 13.6541 10.4514 13.7111 10.6504 13.6863C10.8494 13.6615 11.0297 13.5569 11.15 13.3966Z"
                        fill={
                          activeLink === "/screening" ? "#FFFFFF" : "#003461"
                        }
                      />
                    </svg>

                    <div className="flex flex-col items-start justify-start px-0 pb-0 pt-[5px]">
                      <p className="relative min-w-[40px] font-semibold text-[#003461]">
                        Screening
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="mb-10 flex flex-col gap-2">
                <p className="ml-1 flex text-[25px] font-bold text-[#86181A]">
                  {role === "admin"
                    ? "Admin"
                    : role === "citizen"
                    ? "Citizen"
                    : role === "center"
                    ? "Center"
                    : "Partner"}
                </p>
                <p className="ml-1 text-xs text-gray-800">
                  DARCI, v{packageJson.version}
                </p>
              </div>
            </div>
          </div>
        </div>
        {/* Add version at bottom */}
      </aside>
      {showBlur && (
        <>
          <style>
            {`
              body {
                overflow: hidden;
              }
            `}
          </style>
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-opacity-10 backdrop-blur-md">
            <div className="flex flex-col items-center justify-center rounded-lg p-8">
              <div className="flex w-full flex-col items-center justify-center gap-2 space-x-4">
                <p className="text-[40px] font-bold text-[#348BFF]">
                  {role === "admin"
                    ? "This section is still under development"
                    : role === "center"
                    ? "Please contact DARCI via support@blueskyglobal.io to complete your Center Certification."
                    : role === "citizen" && isCitizenNotActive
                    ? "This section will be available once you have completed your profile."
                    : "Please connect to a DARCI Center to gain full access to the DARCI Platform. If no Center exists in your service area, please reach out to support@blueskyglobal.io to learn how communities can become DARCI READY."}
                </p>
                <button
                  onClick={() => setShowBlur(false)}
                  className="mx-auto flex w-24 cursor-pointer items-center justify-center rounded-lg border border-blue-500 bg-white px-4 py-2 text-center text-blue-500"
                >
                  Okay
                </button>
              </div>
            </div>
          </div>
        </>
      )}
      {showInvite && (
        <InviteFriendDialog onClose={() => setShowInvite(false)} />
      )}
    </div>
  );
};

LeftNavPane.propTypes = {
  className: PropTypes.string,
};

export default LeftNavPane;
